import {
  endOfMonth,
  format,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
} from "date-fns";
import { es } from "date-fns/locale";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

import { supabase } from "~/integrations/supabase/client";
import { bsaleFetch } from "~/services/bsaleProxy";
import { facturaCostsService } from "~/services/facturaCostsService";
import { SalesRequestDocument } from "~/types/client";
import { isPaymentVerified } from "~/utils/checkPaymentUtils";
import { grossFromNet } from "~/utils/iva";
import { calcLineTotal } from "~/utils/lineItemCalculations";

/**
 * Translates payment method from English to Spanish
 */
const translatePaymentMethod = (method: string | null): string => {
  if (!method) return "N/A";

  const translations: Record<string, string> = {
    transfer: "Transferencia bancaria",
    bank_transfer: "Transferencia bancaria",
    cash: "Efectivo",
    check: "Cheque",
    manual: "Manual",
    other: "Otro",
    wire_transfer: "Transferencia bancaria",
    bank: "Transferencia bancaria",
  };

  return translations[method.toLowerCase()] || method;
};

interface FacturaExportData {
  folioFactura: string;
  tipoDocumento: string;
  idNotaVenta: string;
  nombreCliente: string;
  rutCliente: string;
  fechaEmision: string;
  condicionPago: string;
  fechaExpiracion: string;
  montoNeto: number;
  montoBruto: number;
  totalPagado: number;
  fechaPago: string;
  statusPago: string;
  nombreVendedor: string;
  vendedorAnterior: string;
  fechaCambioVendedor: string;
  // Credit note specific columns
  originalFacturaNumber?: string;
  facturaCreationDate?: string;
  cancellationReason?: string;
  // Client status columns
  siniestro: boolean;
  cobranzaJudicial: boolean;
  // Fully-annulled factura: keeps its revenue (nets with its NC) but carries $0 cost.
  cancelled?: boolean;
}

interface MonthlyTotals {
  period: string;
  facturas_amount: number;
  credit_notes_amount: number;
  cross_period_adjustments: number;
  net_amount: number;
  details: any;
}

function batchArray<T>(arr: T[], batchSize: number): T[][] {
  const batches: T[][] = [];
  for (let i = 0; i < arr.length; i += batchSize) {
    batches.push(arr.slice(i, i + batchSize));
  }
  return batches;
}

// Helper functions for credit note column logic
function getCreditNoteOriginalFacturaNumber(
  factura: any,
  enrichedCreditNoteData: Map<string, any>,
  originalFacturasMap: Map<string, any>,
  originalFacturasLookupMap: Map<string, any>,
  originalFacturasByNumberMap: Map<string, any>,
  filteredFacturas: any[]
): string {
  const bsaleNumber = (factura as any).bsale_number?.toString() || "";
  const enrichedData = enrichedCreditNoteData.get(bsaleNumber);
  let originalFacturaNumber = "";

  // Method 1: Try original_document_id from enriched data first
  const originalDocumentId =
    enrichedData?.original_document_id || (factura as any).original_document_id;
  if (originalDocumentId) {
    const originalFactura = originalFacturasMap.get(originalDocumentId);
    if (originalFactura) {
      originalFacturaNumber = originalFactura.bsale_number?.toString() || "N/A";
    }
  }

  // Method 2: Try to extract from BSale response data if no original_document_id
  if (!originalFacturaNumber || originalFacturaNumber === "N/A") {
    const responseData =
      enrichedData?.response_data || (factura as any).response_data;

    if (responseData?.reference_document) {
      const refDoc = responseData.reference_document;
      if (refDoc.number) {
        originalFacturaNumber = refDoc.number.toString();
      }
    }
  }

  // Method 3: Try to extract from reference field (BSale href)
  if (!originalFacturaNumber || originalFacturaNumber === "N/A") {
    const referenceUrl = enrichedData?.reference || (factura as any).reference;
    if (referenceUrl) {
      try {
        const documentIdMatch = referenceUrl.match(/documents\/(\d+)\.json/);
        if (documentIdMatch) {
          const bsaleDocumentId = documentIdMatch[1];
          const matchingFactura = filteredFacturas.find(
            (f) =>
              (f as any).bsale_number?.toString() === bsaleDocumentId ||
              (f as any).external_id?.toString() === bsaleDocumentId
          );

          if (matchingFactura) {
            originalFacturaNumber = bsaleDocumentId;
          }
        }
      } catch (e) {
        console.error("Error parsing reference URL:", e);
      }
    }
  }

  // Method 4: Extract from BSale response_data using reference_document.id
  if (!originalFacturaNumber || originalFacturaNumber === "N/A") {
    const responseData =
      enrichedData?.response_data || (factura as any).response_data;

    if (responseData?.reference_document?.id) {
      const referenceDocumentId = responseData.reference_document.id.toString();

      // First try to find in current filtered facturas
      const matchingFactura = filteredFacturas.find(
        (f) => (f as any).external_id?.toString() === referenceDocumentId
      );

      // If not found in filtered facturas, check our database lookup
      if (!matchingFactura) {
        const facturaFromDb =
          originalFacturasLookupMap.get(referenceDocumentId);
        if (facturaFromDb) {
          originalFacturaNumber =
            facturaFromDb.bsale_number?.toString() || "N/A";
        } else {
          // Method 4b: Fallback to lookup by reference_document.number
          if (responseData?.reference_document?.number) {
            const referenceDocumentNumber =
              responseData.reference_document.number.toString();
            const facturaByNumber = originalFacturasByNumberMap.get(
              referenceDocumentNumber
            );

            if (facturaByNumber) {
              originalFacturaNumber =
                facturaByNumber.bsale_number?.toString() || "N/A";
            }
          }
        }
      } else {
        originalFacturaNumber =
          (matchingFactura as any).bsale_number?.toString() || "N/A";
      }
    }
  }

  // Method 5: Special case for credit notes with same sales_request_id as a factura
  if (
    (!originalFacturaNumber || originalFacturaNumber === "N/A") &&
    factura.salesRequestId
  ) {
    const relatedFactura = filteredFacturas.find(
      (f) =>
        f.documentType === "factura" &&
        f.salesRequestId === factura.salesRequestId
    );

    if (relatedFactura) {
      originalFacturaNumber =
        (relatedFactura as any).bsale_number?.toString() || "N/A";
    }
  }

  return originalFacturaNumber || "N/A";
}

function getCreditNoteFacturaCreationDate(
  factura: any,
  enrichedCreditNoteData: Map<string, any>,
  originalFacturasMap: Map<string, any>,
  originalFacturasLookupMap: Map<string, any>,
  originalFacturasByNumberMap: Map<string, any>,
  filteredFacturas: any[]
): string {
  const bsaleNumber = (factura as any).bsale_number?.toString() || "";
  const enrichedData = enrichedCreditNoteData.get(bsaleNumber);
  let facturaCreationDate = "";

  // Method 1: Try original_document_id from enriched data first
  const originalDocumentId =
    enrichedData?.original_document_id || (factura as any).original_document_id;
  if (originalDocumentId) {
    const originalFactura = originalFacturasMap.get(originalDocumentId);
    if (originalFactura) {
      facturaCreationDate = originalFactura.created_at
        ? format(new Date(originalFactura.created_at), "dd/MM/yyyy", {
            locale: es,
          })
        : "N/A";
    }
  }

  // Method 2: Try to extract from BSale response data if no original_document_id
  if (!facturaCreationDate || facturaCreationDate === "N/A") {
    const responseData =
      enrichedData?.response_data || (factura as any).response_data;

    if (responseData?.reference_document) {
      const refDoc = responseData.reference_document;
      if (refDoc.emissionDate) {
        try {
          facturaCreationDate = format(
            new Date(refDoc.emissionDate),
            "dd/MM/yyyy",
            {
              locale: es,
            }
          );
        } catch (e) {
          console.error("Error parsing emission date:", e);
        }
      }
    }
  }

  // Method 3: Try to extract from reference field (BSale href)
  if (!facturaCreationDate || facturaCreationDate === "N/A") {
    const referenceUrl = enrichedData?.reference || (factura as any).reference;
    if (referenceUrl) {
      try {
        const documentIdMatch = referenceUrl.match(/documents\/(\d+)\.json/);
        if (documentIdMatch) {
          const bsaleDocumentId = documentIdMatch[1];
          const matchingFactura = filteredFacturas.find(
            (f) =>
              (f as any).bsale_number?.toString() === bsaleDocumentId ||
              (f as any).external_id?.toString() === bsaleDocumentId
          );

          if (matchingFactura && matchingFactura.createdAt) {
            facturaCreationDate = format(
              new Date(matchingFactura.createdAt),
              "dd/MM/yyyy",
              {
                locale: es,
              }
            );
          }
        }
      } catch (e) {
        console.error("Error parsing reference URL:", e);
      }
    }
  }

  // Method 4: Extract from BSale response_data using reference_document.id
  if (!facturaCreationDate || facturaCreationDate === "N/A") {
    const responseData =
      enrichedData?.response_data || (factura as any).response_data;

    if (responseData?.reference_document?.id) {
      const referenceDocumentId = responseData.reference_document.id.toString();

      // First try to find in current filtered facturas
      const matchingFactura = filteredFacturas.find(
        (f) => (f as any).external_id?.toString() === referenceDocumentId
      );

      // If not found in filtered facturas, check our database lookup
      if (!matchingFactura) {
        const facturaFromDb =
          originalFacturasLookupMap.get(referenceDocumentId);
        if (facturaFromDb) {
          facturaCreationDate = facturaFromDb.created_at
            ? format(new Date(facturaFromDb.created_at), "dd/MM/yyyy", {
                locale: es,
              })
            : "N/A";
        } else {
          // Method 4b: Fallback to lookup by reference_document.number
          if (responseData?.reference_document?.number) {
            const referenceDocumentNumber =
              responseData.reference_document.number.toString();
            const facturaByNumber = originalFacturasByNumberMap.get(
              referenceDocumentNumber
            );

            if (facturaByNumber) {
              facturaCreationDate = facturaByNumber.created_at
                ? format(new Date(facturaByNumber.created_at), "dd/MM/yyyy", {
                    locale: es,
                  })
                : "N/A";
            }
          }
        }
      } else {
        facturaCreationDate = matchingFactura.createdAt
          ? format(new Date(matchingFactura.createdAt), "dd/MM/yyyy", {
              locale: es,
            })
          : "N/A";
      }
    }
  }

  // Method 5: Special case for credit notes with same sales_request_id as a factura
  if (
    (!facturaCreationDate || facturaCreationDate === "N/A") &&
    factura.salesRequestId
  ) {
    const relatedFactura = filteredFacturas.find(
      (f) =>
        f.documentType === "factura" &&
        f.salesRequestId === factura.salesRequestId
    );

    if (relatedFactura) {
      facturaCreationDate = relatedFactura.createdAt
        ? format(new Date(relatedFactura.createdAt), "dd/MM/yyyy", {
            locale: es,
          })
        : "N/A";
    }
  }

  return facturaCreationDate || "N/A";
}

function getCreditNoteCancellationReason(
  factura: any,
  enrichedCreditNoteData: Map<string, any>
): string {
  const bsaleNumber = (factura as any).bsale_number?.toString() || "";
  const enrichedData = enrichedCreditNoteData.get(bsaleNumber);

  // Get cancellation reason from enriched data first, then fallback
  const cancellationReason =
    enrichedData?.motive ||
    enrichedData?.administrative_reason ||
    (factura as any).motive ||
    (factura as any).administrative_reason ||
    "No especificado";

  return cancellationReason;
}

/**
 * Enriches facturas data with additional fields needed for Excel export
 * This function does NOT filter data - it only adds missing fields
 * Use this to ensure Excel export uses the same data as UI statistics
 */
export const enrichFacturasForExport = async (
  facturas: SalesRequestDocument[],
  startDate?: Date,
  endDate?: Date,
  userRole?: string,
  currentUserId?: string
): Promise<FacturaExportData[]> => {
  try {
    // Get unique sales request IDs and client IDs from the already-filtered facturas
    const salesRequestIds = facturas
      .map((f) => f.salesRequestId)
      .filter(Boolean) as string[];
    const clientIds = facturas
      .map((f) => f.clientId)
      .filter(Boolean) as string[];

    const BATCH_SIZE = 100;

    // Batch fetch sales requests (only for enrichment, no filtering)
    let salesRequests: any[] = [];
    for (const batch of batchArray(salesRequestIds, BATCH_SIZE)) {
      const { data, error } = await supabase
        .from("sales_request")
        .select(
          `
          id,
          client_id,
          created_at,
          payment_due_date,
          total_amount,
          price_with_iva,
          is_exento,
          status,
          paid,
          paid_amount,
          created_by,
          sales_condition_id,
          factura_creation_date,
          sales_conditions:sales_condition_id(name, maximum_days),
          profiles:created_by(name, lastname),
          clients:client_id(
            id,
            name,
            is_test,
            sales_condition_id,
            sales_conditions:sales_condition_id(name, maximum_days)
          )
        `
        )
        .in("id", batch);
      if (error) throw error;
      salesRequests = salesRequests.concat(data || []);
    }

    // Batch fetch clients (only for enrichment)
    let clients: any[] = [];
    for (const batch of batchArray(clientIds, BATCH_SIZE)) {
      const { data, error } = await supabase
        .from("clients")
        .select(
          `
          id,
          name,
          tax_id,
          sales_condition_id,
          sales_conditions:sales_condition_id(name, maximum_days)
        `
        )
        .in("id", batch);
      if (error) throw error;
      clients = clients.concat(data || []);
    }

    // Batch fetch payments (only for enrichment)
    let payments: any[] = [];
    for (const batch of batchArray(salesRequestIds, BATCH_SIZE)) {
      const { data, error } = await supabase
        .from("payments")
        .select(
          "sales_request, amount, created_at, payment_method, is_verified, check_status"
        )
        .in("sales_request", batch);
      if (error) throw error;
      payments = payments.concat(data || []);
    }

    // Create lookup maps for enrichment
    const salesRequestMap = new Map(
      salesRequests?.map((sr) => [sr.id, sr]) || []
    );
    const clientMap = new Map(clients?.map((c) => [c.id, c]) || []);

    // Create payments map
    const paymentsMap = new Map<string, number>();
    const paymentDatesMap = new Map<string, Date>();

    payments?.forEach((payment) => {
      // Use consistent payment verification logic
      // For checks: only 'collected' or 'paid' count
      // For other methods: use is_verified
      if (
        isPaymentVerified(
          payment.payment_method,
          payment.is_verified,
          payment.check_status
        )
      ) {
        const currentTotal = paymentsMap.get(payment.sales_request) || 0;
        paymentsMap.set(
          payment.sales_request,
          currentTotal + (payment.amount || 0)
        );

        if (payment.created_at) {
          const paymentDate = new Date(payment.created_at);
          const existingDate = paymentDatesMap.get(payment.sales_request);
          if (!existingDate || paymentDate > existingDate) {
            paymentDatesMap.set(payment.sales_request, paymentDate);
          }
        }
      }
    });

    // Credit note enrichment logic
    const allCreditNotes = facturas.filter(
      (f) => f.documentType === "nota_de_credito"
    );
    const creditNoteBsaleNumbers = allCreditNotes
      .map((f) => (f as any).bsale_number)
      .filter(Boolean);
    let enrichedCreditNoteData = new Map<string, any>();

    if (creditNoteBsaleNumbers.length > 0) {
      const { data: creditNoteDetails, error: creditNoteError } = await supabase
        .from("sales_request_documents")
        .select("*")
        .in("bsale_number", creditNoteBsaleNumbers);

      if (!creditNoteError && creditNoteDetails) {
        enrichedCreditNoteData = new Map(
          creditNoteDetails.map((cn: any) => [
            cn.bsale_number?.toString() || "",
            cn,
          ])
        );
      }
    }

    // Collect all reference_document.id values and reference_document.number values that we need to look up
    const referenceIdsToLookup = new Set<string>();
    const referenceNumbersToLookup = new Set<string>();
    enrichedCreditNoteData.forEach((creditNoteData) => {
      if (creditNoteData.response_data?.reference_document?.id) {
        referenceIdsToLookup.add(
          creditNoteData.response_data.reference_document.id.toString()
        );
      }
      if (creditNoteData.response_data?.reference_document?.number) {
        referenceNumbersToLookup.add(
          creditNoteData.response_data.reference_document.number.toString()
        );
      }
    });

    // Collect original_document_id values for direct database lookup
    const originalDocumentIdsToLookup = new Set<string>();
    enrichedCreditNoteData.forEach((creditNoteData) => {
      if (creditNoteData.original_document_id) {
        originalDocumentIdsToLookup.add(
          creditNoteData.original_document_id.toString()
        );
      }
    });

    // Also add from factura data
    facturas.forEach((factura) => {
      if ((factura as any).original_document_id) {
        originalDocumentIdsToLookup.add(
          (factura as any).original_document_id.toString()
        );
      }
    });

    // Create maps for original factura lookup
    const originalFacturasMap = new Map<string, any>();
    const originalFacturasLookupMap = new Map<string, any>();
    const originalFacturasByNumberMap = new Map<string, any>();

    // Populate originalFacturasMap with facturas by their ID for original_document_id lookup
    facturas.forEach((factura) => {
      if (factura.id) {
        originalFacturasMap.set(factura.id, factura);
      }
    });

    // Lookup original facturas by their UUID (original_document_id)
    if (originalDocumentIdsToLookup.size > 0) {
      const originalDocumentIdsArray = Array.from(originalDocumentIdsToLookup);

      const { data: originalFacturas, error: originalFacturasError } =
        await supabase
          .from("sales_request_documents")
          .select("*")
          .in("id", originalDocumentIdsArray);

      if (!originalFacturasError && originalFacturas) {
        originalFacturas.forEach((factura) => {
          // Add to originalFacturasMap using the factura's ID
          originalFacturasMap.set(factura.id, factura);
        });
      }
    }

    // Lookup facturas by external_id (BSale document ID) for reference_document.id
    if (referenceIdsToLookup.size > 0) {
      const referenceIdsArray = Array.from(referenceIdsToLookup);
      const { data: referencedFacturas, error: refError } = await supabase
        .from("sales_request_documents")
        .select("*")
        .in("external_id", referenceIdsArray);

      if (!refError && referencedFacturas) {
        referencedFacturas.forEach((factura) => {
          originalFacturasLookupMap.set(
            factura.external_id?.toString() || "",
            factura
          );
        });
      }
    }

    // Lookup facturas by bsale_number for reference_document.number
    if (referenceNumbersToLookup.size > 0) {
      const referenceNumbersArray = Array.from(referenceNumbersToLookup);
      const { data: referencedFacturasByNumber, error: refNumberError } =
        await supabase
          .from("sales_request_documents")
          .select("*")
          .in("bsale_number", referenceNumbersArray);

      if (!refNumberError && referencedFacturasByNumber) {
        referencedFacturasByNumber.forEach((factura) => {
          originalFacturasByNumberMap.set(
            factura.bsale_number?.toString() || "",
            factura
          );
        });
      }
    }

    // Count facturas by type for debugging
    const facturasCountByType = {
      factura: facturas.filter((f) => f.documentType === "factura").length,
      nota_de_credito: facturas.filter(
        (f) => f.documentType === "nota_de_credito"
      ).length,
      nota_de_debito: facturas.filter(
        (f) => f.documentType === "nota_de_debito"
      ).length,
      other: facturas.filter(
        (f) =>
          !["factura", "nota_de_credito", "nota_de_debito"].includes(
            f.documentType
          )
      ).length,
    };

    // Transform facturas data to Excel format (NO filtering, only enrichment)
    const exportData: FacturaExportData[] = facturas.map((factura) => {
      const salesRequest = salesRequestMap.get(factura.salesRequestId || "");
      const client = clientMap.get(factura.clientId || "");

      // Get document type and bsale number
      const documentType = factura.documentType;
      const bsaleNumber = (factura as any).bsale_number?.toString() || "";

      // Use the same amounts that FacturasStatistics uses (already enhanced in optimized service)
      let montoNeto = 0;
      let montoBruto = 0;

      if (documentType === "nota_de_credito") {
        // For credit notes, use enhanced net_amount from optimized service (NEGATIVE in Excel)
        const amount =
          (factura as any).net_amount || (factura as any).total_amount || 0;
        montoNeto = Math.round(-Math.abs(parseFloat(amount.toString()) || 0)); // Negative for credit notes (rounded)
        montoBruto = grossFromNet(montoNeto, salesRequest?.is_exento); // Negative; exempt = no IVA
      } else if (documentType === "nota_de_debito") {
        // For debit notes, use enhanced net_amount from optimized service (POSITIVE in Excel)
        const amount = (factura as any).net_amount || 0;
        montoNeto = Math.round(Math.abs(parseFloat(amount.toString()) || 0)); // Positive for debit notes (rounded)
        montoBruto = grossFromNet(montoNeto, salesRequest?.is_exento); // Positive; exempt = no IVA
      } else {
        // For regular facturas, use net_amount from sales_request_documents (via optimized view)
        // This ensures we show BSale PDF values, not discounted values from sales_request
        const amount =
          (factura as any).net_amount ||
          (factura as any).total_amount ||
          salesRequest?.total_amount ||
          0;
        montoNeto = Math.round(parseFloat(amount.toString()) || 0); // Rounded
        // Gross (con IVA) from the net; facturas exentas (SII 34) carry no IVA -> gross = net
        montoBruto = grossFromNet(montoNeto, salesRequest?.is_exento);
      }

      // Get payment info
      let totalPagado = 0;
      let fechaPago = "";
      let statusPago = "Pendiente";

      if (
        documentType === "nota_de_credito" ||
        documentType === "nota_de_debito"
      ) {
        totalPagado = 0;
        statusPago = "N/A";
      } else {
        // Enhanced payment status logic (same as main export function)
        const bsaleNumber = (factura as any).bsale_number?.toString() || "";
        const isProblematicFactura = ["236780", "236441", "238998"].includes(
          bsaleNumber
        );

        // Use enhanced payment status logic that considers multiple sources
        let isPaid = false;

        // Primary: Use optimized view data
        if ((factura as any).sales_request_paid !== undefined) {
          isPaid = (factura as any).sales_request_paid;
        }
        // Fallback 1: Use sales request lookup data
        else if (salesRequest?.paid !== undefined) {
          isPaid = salesRequest.paid;
        }
        // Fallback 2: Calculate based on paid amount vs total amount
        else {
          const currentPaidAmount =
            (factura as any).paid_amount || salesRequest?.paid_amount || 0;
          const totalAmount =
            montoBruto || salesRequest?.price_with_iva || montoNeto || 0;
          isPaid = currentPaidAmount >= totalAmount && totalAmount > 0;
        }

        // Enhanced paid amount calculation
        // If the sales request is marked as paid but paid_amount is 0,
        // it means it was paid via credit notes, so show the total amount as paid
        const basePaidAmount = (factura as any).paid_amount || 0;
        if (isPaid && basePaidAmount === 0) {
          // Payment via credit notes - show total amount as paid for display
          totalPagado =
            montoBruto || salesRequest?.price_with_iva || montoNeto || 0;
        } else {
          // Regular cash payment or partial payment
          totalPagado = basePaidAmount;
        }

        const latestPaymentDate = paymentDatesMap.get(
          factura.salesRequestId || ""
        );
        if (latestPaymentDate) {
          fechaPago = format(latestPaymentDate, "dd/MM/yyyy", { locale: es });
        }

        if (isPaid) {
          statusPago = "Pagado";
        } else if (totalPagado > 0) {
          statusPago = "Parcial";
        } else {
          statusPago = "Pendiente";
        }

        if (isProblematicFactura) {
          console.log(`🔍 ENRICH PAYMENT DEBUG - Factura ${bsaleNumber}:`, {
            sales_request_paid: (factura as any).sales_request_paid,
            salesRequest_paid: salesRequest?.paid,
            basePaidAmount: basePaidAmount,
            enhancedTotalPagado: totalPagado,
            montoBruto,
            isPaid,
            finalStatusPago: statusPago,
          });
        }
      }

      // Get sales condition
      let condicionPago = "Contado";
      if (salesRequest?.sales_conditions?.name) {
        condicionPago = salesRequest.sales_conditions.name;
      } else if (client?.sales_conditions?.name) {
        condicionPago = client.sales_conditions.name;
      }

      // Calculate payment due date
      let fechaExpiracion = "";

      // Special handling for credit notes: expire one day after creation
      if (documentType === "nota_de_credito" && factura.createdAt) {
        try {
          const creationDate = new Date(factura.createdAt);
          if (!isNaN(creationDate.getTime())) {
            const expirationDate = new Date(creationDate);
            expirationDate.setDate(expirationDate.getDate() + 1);
            fechaExpiracion = format(expirationDate, "dd/MM/yyyy", {
              locale: es,
            });
          }
        } catch (error) {
          console.error("Error calculating expiration for credit note:", error);
        }
      } else if (salesRequest?.payment_due_date) {
        fechaExpiracion = format(
          new Date(salesRequest.payment_due_date),
          "dd/MM/yyyy",
          { locale: es }
        );
      }

      return {
        folioFactura: bsaleNumber,
        tipoDocumento:
          documentType === "factura"
            ? "Factura"
            : documentType === "nota_de_credito"
              ? "Nota de Crédito"
              : documentType === "nota_de_debito"
                ? "Nota de Débito"
                : "Documento",
        idNotaVenta: factura.salesRequestId || "",
        nombreCliente: (factura as any).client_name || client?.name || "",
        rutCliente: (factura as any).client_tax_id || client?.tax_id || "",
        fechaEmision: format(new Date(factura.createdAt), "dd/MM/yyyy", {
          locale: es,
        }),
        condicionPago,
        fechaExpiracion,
        montoNeto,
        montoBruto,
        totalPagado,
        fechaPago,
        statusPago,
        nombreVendedor: salesRequest?.profiles?.name
          ? `${salesRequest.profiles.name} ${salesRequest.profiles.lastname || ""}`.trim()
          : "",
        vendedorAnterior: "",
        fechaCambioVendedor: "",
        // Credit note specific columns
        originalFacturaNumber:
          documentType === "nota_de_credito"
            ? getCreditNoteOriginalFacturaNumber(
                factura,
                enrichedCreditNoteData,
                originalFacturasMap,
                originalFacturasLookupMap,
                originalFacturasByNumberMap,
                facturas
              )
            : undefined,
        facturaCreationDate:
          documentType === "nota_de_credito"
            ? getCreditNoteFacturaCreationDate(
                factura,
                enrichedCreditNoteData,
                originalFacturasMap,
                originalFacturasLookupMap,
                originalFacturasByNumberMap,
                facturas
              )
            : undefined,
        cancellationReason:
          documentType === "nota_de_credito"
            ? getCreditNoteCancellationReason(factura, enrichedCreditNoteData)
            : undefined,
        cancelled: (factura as any).cancelled === true,
      };
    });

    // Count output by type for debugging
    const outputCountByType = {
      factura: exportData.filter((f) => f.tipoDocumento === "Factura").length,
      nota_de_credito: exportData.filter(
        (f) => f.tipoDocumento === "Nota de Crédito"
      ).length,
      nota_de_debito: exportData.filter(
        (f) => f.tipoDocumento === "Nota de Débito"
      ).length,
      other: exportData.filter(
        (f) =>
          !["Factura", "Nota de Crédito", "Nota de Débito"].includes(
            f.tipoDocumento
          )
      ).length,
    };

    // Log any count discrepancies
    if (exportData.length !== facturas.length) {
      console.error(
        `❌ EXCEL ENRICHMENT: COUNT MISMATCH! Input: ${facturas.length}, Output: ${exportData.length}`
      );
    }

    return exportData;
  } catch (error) {
    console.error(
      "❌ EXCEL ENRICHMENT: Error in enrichFacturasForExport:",
      error
    );
    throw error;
  }
};

/**
 * Fetches detailed data for facturas export including related sales request and client information
 * @deprecated Use enrichFacturasForExport for better consistency with UI data
 */
export const fetchFacturasForExport = async (
  facturas: SalesRequestDocument[],
  startDate?: Date,
  endDate?: Date,
  userRole?: string,
  currentUserId?: string
): Promise<FacturaExportData[]> => {
  try {
    // Get unique sales request IDs and client IDs
    const salesRequestIds = facturas
      .map((f) => f.salesRequestId)
      .filter(Boolean) as string[];
    const clientIds = facturas
      .map((f) => f.clientId)
      .filter(Boolean) as string[];

    console.log("Unique sales request IDs:", salesRequestIds.length);
    console.log("Unique client IDs:", clientIds.length);

    // REMOVED: Debug logging for factura 235899 - issue was hard-coded corrections

    const BATCH_SIZE = 100; // or 200, depending on your needs

    // Batch fetch sales requests
    let salesRequests: any[] = [];
    for (const batch of batchArray(salesRequestIds, BATCH_SIZE)) {
      const { data, error } = await supabase
        .from("sales_request")
        .select(
          `
          id,
          client_id,
          created_at,
          payment_due_date,
          total_amount,
          price_with_iva,
          is_exento,
          status,
          paid,
          paid_amount,
          created_by,
          sales_condition_id,
          factura_creation_date,
          sales_conditions:sales_condition_id(name, maximum_days),
          profiles:created_by(name, lastname),
          clients:client_id(
            id,
            name,
            is_test,
            sales_condition_id,
            sales_conditions:sales_condition_id(name, maximum_days)
          )
        `
        )
        .in("id", batch)
        .in("status", [
          "accepted",
          "delivered",
          "in_transit",
          "pending",
          "pending_pricing",
          "pending_credit",
          "dispatch_ready",
          "prepaid",
          "payment_to_check",
          "rejected",
          "por_facturar",
          // Note: "test" status is intentionally excluded from exports
        ]);
      if (error) throw error;
      salesRequests = salesRequests.concat(data || []);
    }

    // Batch fetch clients
    let clients: any[] = [];
    for (const batch of batchArray(clientIds, BATCH_SIZE)) {
      const { data, error } = await supabase
        .from("clients")
        .select(
          `
          id,
          name,
          tax_id,
          siniestro,
          cobranza_judicial,
          sales_condition_id,
          sales_conditions:sales_condition_id(name, maximum_days)
        `
        )
        .in("id", batch);
      if (error) throw error;
      clients = clients.concat(data || []);
    }

    // Batch fetch previous sellers for clients
    let previousSellers: any[] = [];
    for (const batch of batchArray(clientIds, BATCH_SIZE)) {
      const { data, error } = await supabase
        .from("client_seller_history")
        .select(
          `
          client_id,
          seller_id,
          assigned_at,
          unassigned_at,
          profiles:seller_id(name, lastname)
        `
        )
        .in("client_id", batch)
        .is("unassigned_at", null) // Get current assignments
        .order("assigned_at", { ascending: false });

      if (error) {
        console.warn("Error fetching previous sellers:", error);
        // Continue without previous seller data if there's an error
      } else {
        previousSellers = previousSellers.concat(data || []);
      }
    }

    // Also fetch the most recent previous sellers (unassigned ones)
    let recentPreviousSellers: any[] = [];
    for (const batch of batchArray(clientIds, BATCH_SIZE)) {
      const { data, error } = await supabase
        .from("client_seller_history")
        .select(
          `
          client_id,
          seller_id,
          assigned_at,
          unassigned_at,
          profiles:seller_id(name, lastname)
        `
        )
        .in("client_id", batch)
        .not("unassigned_at", "is", null) // Get unassigned (previous) sellers
        .order("unassigned_at", { ascending: false });

      if (error) {
        console.warn("Error fetching recent previous sellers:", error);
      } else {
        recentPreviousSellers = recentPreviousSellers.concat(data || []);
      }
    }

    // Batch fetch payments
    let payments: any[] = [];
    for (const batch of batchArray(salesRequestIds, BATCH_SIZE)) {
      const { data, error } = await supabase
        .from("payments")
        .select(
          "sales_request, amount, created_at, payment_method, is_verified, check_status"
        )
        .in("sales_request", batch);
      if (error) throw error;
      payments = payments.concat(data || []);
    }

    // Fetch original factura information for credit notes
    const creditNoteOriginalDocumentIds = facturas
      .filter((f) => f.documentType === "nota_de_credito")
      .map((f) => (f as any).original_document_id)
      .filter(Boolean) as string[];

    let originalFacturasMap = new Map<string, any>();
    if (creditNoteOriginalDocumentIds.length > 0) {
      const { data: originalFacturas, error: originalFacturasError } =
        await supabase
          .from("sales_request_documents")
          .select("id, bsale_number, created_at")
          .in("id", creditNoteOriginalDocumentIds);

      if (!originalFacturasError && originalFacturas) {
        originalFacturasMap = new Map(originalFacturas.map((f) => [f.id, f]));
      }
    }

    // Create lookup maps
    const salesRequestMap = new Map(
      salesRequests?.map((sr) => [sr.id, sr]) || []
    );
    const clientMap = new Map(clients?.map((c) => [c.id, c]) || []);

    // Create previous sellers lookup map
    const previousSellersMap = new Map<string, string>();
    // Create seller change dates lookup map
    const sellerChangeDatesMap = new Map<string, string>();

    // Group previous sellers by client_id and get the most recent previous seller
    const sellerHistoryByClient = new Map<string, any[]>();

    // Combine all seller history data
    const allSellerHistory = [...previousSellers, ...recentPreviousSellers];

    allSellerHistory.forEach((history) => {
      const clientId = history.client_id;
      if (!sellerHistoryByClient.has(clientId)) {
        sellerHistoryByClient.set(clientId, []);
      }
      sellerHistoryByClient.get(clientId)!.push(history);
    });

    // For each client, find the most recent previous seller (excluding current seller)
    sellerHistoryByClient.forEach((histories, clientId) => {
      // Sort by assigned_at in descending order (most recent first)
      const sortedHistories = histories.sort((a, b) => {
        const dateA = new Date(a.assigned_at).getTime();
        const dateB = new Date(b.assigned_at).getTime();
        return dateB - dateA;
      });

      // Find the current client to get their current seller
      const currentClient = clientMap.get(clientId);
      const currentSellerId = currentClient?.assigned_seller;

      // Find the most recent truly previous seller (one that has been unassigned)
      const truePreviousSeller = sortedHistories.find((history) => {
        return history.unassigned_at; // Only consider sellers that have been actually unassigned
      });

      let changeDate = "Sin fecha";

      if (truePreviousSeller && truePreviousSeller.profiles) {
        // We found a seller that was actually unassigned (changed)
        const name = truePreviousSeller.profiles.name || "";
        const lastname = truePreviousSeller.profiles.lastname || "";
        const fullName = `${name} ${lastname}`.trim();
        previousSellersMap.set(clientId, fullName || "Vendedor Anterior");

        changeDate = format(
          new Date(truePreviousSeller.unassigned_at),
          "dd/MM/yyyy",
          {
            locale: es,
          }
        );
      } else {
        // No seller has been unassigned yet, so use the current seller's assignment date
        const currentSellerHistory = sortedHistories.find(
          (history) =>
            history.seller_id === currentSellerId && !history.unassigned_at
        );

        if (currentSellerHistory) {
          changeDate = format(
            new Date(currentSellerHistory.assigned_at),
            "dd/MM/yyyy",
            {
              locale: es,
            }
          );
        } else {
          // Try to find any seller assignment for this client
          const anySellerHistory = sortedHistories[0]; // Most recent
          if (anySellerHistory) {
            changeDate = format(
              new Date(anySellerHistory.assigned_at),
              "dd/MM/yyyy",
              {
                locale: es,
              }
            );
          }
        }
      }

      sellerChangeDatesMap.set(clientId, changeDate);
    });

    // Create payments map - sum all payments per sales request and track latest payment date
    const paymentsMap = new Map<string, number>();
    const paymentDatesMap = new Map<string, Date>();

    // First, deduplicate payments that have the same sales_request, amount, and timestamp
    const uniquePayments = new Map<string, any>();
    payments?.forEach((payment) => {
      if (payment.sales_request) {
        // Create a unique key based on sales_request, amount, and timestamp
        const paymentKey = `${payment.sales_request}_${payment.amount}_${payment.created_at}`;

        // Only keep the first occurrence of each unique payment
        if (!uniquePayments.has(paymentKey)) {
          uniquePayments.set(paymentKey, payment);
        }
      }
    });

    // Process the deduplicated payments
    uniquePayments.forEach((payment) => {
      if (payment.sales_request) {
        // Only include verified payments in the total calculation
        let isVerified = false;

        if (payment.payment_method === "check") {
          // For check payments, use check_status
          isVerified =
            payment.check_status === "collected" ||
            payment.check_status === "paid";
        } else {
          // For non-check payments, use is_verified field
          isVerified = payment.is_verified === true;
        }

        if (isVerified) {
          const currentTotal = paymentsMap.get(payment.sales_request) || 0;
          paymentsMap.set(
            payment.sales_request,
            currentTotal + (payment.amount || 0)
          );

          // Track the latest payment date
          if (payment.created_at) {
            const paymentDate = new Date(payment.created_at);
            const existingDate = paymentDatesMap.get(payment.sales_request);
            if (!existingDate || paymentDate > existingDate) {
              paymentDatesMap.set(payment.sales_request, paymentDate);
            }
          }
        }
      }
    });

    // Filter facturas to include those with valid sales requests (with allowed statuses) OR standalone credit notes
    const validSalesRequestIds = new Set(
      salesRequests?.map((sr) => sr.id) || []
    );

    // Add sales request IDs for specific July credit notes to ensure they're included
    const targetCreditNotes = facturas.filter(
      (f) =>
        f.documentType === "nota_de_credito" &&
        ["10636", "10637"].includes((f as any).bsale_number?.toString())
    );

    targetCreditNotes.forEach((cn) => {
      if (cn.salesRequestId) {
        validSalesRequestIds.add(cn.salesRequestId);
      }
    });

    // For sellers, get their assigned clients if needed
    let sellerClientIds = new Set<string>();
    if (userRole === "seller" && currentUserId) {
      try {
        const { data: sellerClients, error } = await supabase
          .from("clients")
          .select("id")
          .eq("assigned_seller", currentUserId);

        if (!error && sellerClients) {
          sellerClientIds = new Set(sellerClients.map((c) => c.id));
        }
      } catch (error) {
        console.error("Error fetching seller's assigned clients:", error);
      }
    }

    // Debug: Check if target credit notes are in the facturas array
    console.log(
      `🔍 Excel Export - All facturas BSale numbers:`,
      facturas.map((f) => (f as any).bsale_number).filter(Boolean)
    );

    const receivedCreditNotes = facturas.filter(
      (f) => f.documentType === "nota_de_credito"
    );
    console.log(
      `🔍 Excel Export - Total credit notes received: ${receivedCreditNotes.length}`
    );
    console.log(
      `🔍 Excel Export - Credit notes BSale numbers:`,
      receivedCreditNotes.map((f) => (f as any).bsale_number)
    );

    const receivedTargetCreditNotes = receivedCreditNotes.filter((f) =>
      ["10636", "10637"].includes((f as any).bsale_number?.toString())
    );
    console.log(
      `🔍 Excel Export - Target July credit notes in data: ${receivedTargetCreditNotes.length}`
    );
    if (receivedTargetCreditNotes.length > 0) {
      console.log(
        `🔍 Excel Export - Target credit notes details:`,
        receivedTargetCreditNotes.map((cn) => ({
          bsaleNumber: (cn as any).bsale_number,
          documentType: cn.documentType,
          isTemporary: cn.isTemporary,
          salesRequestId: cn.salesRequestId,
        }))
      );

      // Check if their sales request IDs are in validSalesRequestIds
      receivedTargetCreditNotes.forEach((cn) => {
        const salesRequestId = cn.salesRequestId;
        const isValidSalesRequest = salesRequestId
          ? validSalesRequestIds.has(salesRequestId)
          : false;
        console.log(
          `🔍 Excel Export - Credit note ${(cn as any).bsale_number}:`
        );
        console.log(`  - Sales Request ID: ${salesRequestId || "None"}`);
        console.log(`  - Is in validSalesRequestIds: ${isValidSalesRequest}`);
        if (salesRequestId && !isValidSalesRequest) {
          console.log(
            `  ❌ This is why it will be filtered out - sales request ID not valid`
          );
        }
      });
    }

    const filteredFacturas = facturas.filter((factura) => {
      const bsaleNumber = (factura as any).bsale_number?.toString();

      // Debug for seller filtering
      if (userRole === "seller" && bsaleNumber) {
        console.log(
          `🔍 EXPORT FILTER DEBUG: Processing doc ${bsaleNumber}, type=${factura.documentType}, isTemporary=${factura.isTemporary}, salesRequestId=${factura.salesRequestId}`
        );
      }

      // Special debug for credit note 10475
      if (bsaleNumber === "10475") {
        console.log(`🔍 CREDIT NOTE 10475 DEBUG: Processing credit note 10475`);
        console.log(`  - documentType: ${factura.documentType}`);
        console.log(`  - isTemporary: ${factura.isTemporary}`);
        console.log(`  - salesRequestId: ${factura.salesRequestId}`);
        console.log(`  - createdAt: ${factura.createdAt}`);
        console.log(`  - userRole: ${userRole}`);
        console.log(`  - currentUserId: ${currentUserId}`);
      }

      // SECURITY: Sellers should only see facturas, not credit notes or debit notes
      if (userRole === "seller" && factura.documentType !== "factura") {
        console.log(
          `🚫 SELLER FILTER: Excluding ${factura.documentType} ${bsaleNumber} - sellers only see facturas`
        );
        if (bsaleNumber === "10475") {
          console.log(`❌ CREDIT NOTE 10475 EXCLUDED: Seller role filter`);
        }
        return false;
      }

      // EXCLUDE test factura 236885
      if (bsaleNumber === "236885") {
        if (userRole === "seller") {
          console.log(
            `🚫 EXPORT FILTER: Excluding test factura ${bsaleNumber}`
          );
        }
        return false;
      }

      // Debug for credit note 10475 at each filter step
      if (bsaleNumber === "10475") {
        console.log(`🔍 CREDIT NOTE 10475: Passed test factura filter`);
      }

      // ABSOLUTE FIRST: Check target credit notes with seller filtering AND month filtering
      if (bsaleNumber === "10636" || bsaleNumber === "10637") {
        // Check if the credit note was created within the date range
        if (startDate && endDate) {
          const documentDate = new Date(factura.createdAt);

          const isWithinDateRange =
            documentDate >= startDate && documentDate <= endDate;

          if (!isWithinDateRange) {
            return false;
          }
        }

        // For sellers, only include if it's from their sales request
        if (userRole === "seller" && currentUserId) {
          return (
            factura.salesRequestId &&
            validSalesRequestIds.has(factura.salesRequestId)
          );
        }
        return true;
      }

      // SECOND: Special bypass for specific July credit notes - but respect seller filtering AND month filtering
      if (["10636", "10637"].includes(bsaleNumber)) {
        // Check if the credit note was created within the date range
        if (startDate && endDate) {
          const documentDate = new Date(factura.createdAt);

          const isWithinDateRange =
            documentDate >= startDate && documentDate <= endDate;

          if (!isWithinDateRange) {
            return false;
          }
        }

        // For sellers, exclude these credit notes
        if (userRole === "seller") {
          return false;
        }
        return true;
      }

      // Secondary bypass: More specific check for credit notes - but respect seller filtering AND month filtering
      if (
        factura.documentType === "nota_de_credito" &&
        ["10636", "10637"].includes(bsaleNumber)
      ) {
        // Check if the credit note was created within the date range
        if (startDate && endDate) {
          const documentDate = new Date(factura.createdAt);

          const isWithinDateRange =
            documentDate >= startDate && documentDate <= endDate;

          if (!isWithinDateRange) {
            return false;
          }
        }

        // For sellers, exclude these credit notes
        if (userRole === "seller") {
          return false;
        }
        return true;
      }

      // Special handling for July 2025+ documents (bypass normal validation)
      if (startDate && endDate && bsaleNumber) {
        const isFilteringJuly2025OrLater =
          (startDate.getFullYear() === 2025 && startDate.getMonth() >= 6) ||
          startDate.getFullYear() > 2025;

        // For July 2025+ credit notes, include credit notes created in the same month as the filter
        if (
          factura.documentType === "nota_de_credito" &&
          isFilteringJuly2025OrLater
        ) {
          // Check if the credit note was created in the same month as the filter
          const documentDate = new Date(factura.createdAt);
          const documentYear = documentDate.getFullYear();
          const documentMonth = documentDate.getMonth();
          const filterYear = startDate.getFullYear();
          const filterMonth = startDate.getMonth();

          const isWithinDateRange =
            documentDate >= startDate && documentDate <= endDate;

          if (!isWithinDateRange) {
            return false;
          }

          // For sellers, still apply filtering - only include their own credit notes with sales requests
          if (userRole === "seller") {
            return (
              factura.salesRequestId &&
              validSalesRequestIds.has(factura.salesRequestId)
            );
          }
          return true;
        }

        // For specific July credit notes, include them but respect seller filtering AND month filtering
        if (
          factura.documentType === "nota_de_credito" &&
          ["10636", "10637", "10594"].includes(bsaleNumber)
        ) {
          // Check if the credit note was created in the same month as the filter
          const documentDate = new Date(factura.createdAt);
          const documentYear = documentDate.getFullYear();
          const documentMonth = documentDate.getMonth();
          const filterYear = startDate.getFullYear();
          const filterMonth = startDate.getMonth();

          const isWithinDateRange =
            documentDate >= startDate && documentDate <= endDate;

          if (!isWithinDateRange) {
            return false;
          }

          // For sellers, exclude these credit notes
          if (userRole === "seller") {
            return false;
          }
          return true;
        }

        // For July 2025 ONLY facturas, include specific SII-declared facturas (236884, 236883)
        // These should only appear in July 2025 reports, not later months
        const isFilteringJuly2025Only =
          startDate &&
          endDate &&
          startDate.getFullYear() === 2025 &&
          startDate.getMonth() === 6 &&
          endDate.getMonth() === 6;

        if (
          factura.documentType === "factura" &&
          isFilteringJuly2025Only &&
          ["236884", "236883"].includes(bsaleNumber)
        ) {
          return true;
        }
      }

      // Include facturas with valid sales request IDs (and exclude test clients)
      if (
        factura.salesRequestId &&
        validSalesRequestIds.has(factura.salesRequestId)
      ) {
        // Exclude test clients
        const salesRequest = salesRequestMap.get(factura.salesRequestId);
        if (salesRequest && (salesRequest as any).clients?.is_test) {
          // Debug for target credit notes
          if (["10636", "10637"].includes(bsaleNumber)) {
            console.log(
              `❌ Excel Export - Target credit note ${bsaleNumber} EXCLUDED: Test client`
            );
          }
          if (bsaleNumber === "10475") {
            console.log(`❌ CREDIT NOTE 10475 EXCLUDED: Test client`);
          }
          return false;
        }

        // Debug for target credit notes
        if (["10636", "10637"].includes(bsaleNumber)) {
          console.log(
            `✅ Excel Export - Target credit note ${bsaleNumber} INCLUDED: Valid sales request`
          );
        }
        if (bsaleNumber === "10475") {
          console.log(`✅ CREDIT NOTE 10475 INCLUDED: Valid sales request`);
        }
        return true;
      }

      // Include standalone credit notes (marked as temporary)
      const isStandaloneCredit =
        !factura.salesRequestId &&
        factura.isTemporary === true &&
        factura.documentType === "nota_de_credito";

      if (isStandaloneCredit) {
        if (bsaleNumber === "10475") {
          console.log(
            `🔍 CREDIT NOTE 10475: Processing as standalone credit note`
          );
        }
        if (userRole === "seller") {
          console.log(
            `🚫 EXPORT FILTER: Excluding standalone credit note ${bsaleNumber} for seller`
          );
        }
        // Special case: Include specific July credit notes but respect seller restrictions
        if (bsaleNumber && ["10636", "10637"].includes(bsaleNumber)) {
          // For sellers, exclude these BSale Directo credit notes
          if (userRole === "seller") {
            return false;
          }
          return true;
        }

        // For sellers, exclude all standalone BSale Directo credit notes
        if (userRole === "seller" && currentUserId) {
          console.log(
            `🚫 EXPORT FILTER: EXCLUDING standalone credit note ${bsaleNumber} for seller ${currentUserId}`
          );
          return false;
        }

        // For non-sellers, include all standalone credit notes
        console.log(
          `✅ EXPORT FILTER: INCLUDING standalone credit note ${bsaleNumber} for non-seller role ${userRole}`
        );
        if (bsaleNumber === "10475") {
          console.log(
            `✅ CREDIT NOTE 10475 INCLUDED: Standalone credit note for non-seller`
          );
        }
        return true;
      }

      if (userRole === "seller") {
        console.log(
          `🚫 EXPORT FILTER: FINAL EXCLUSION for doc ${bsaleNumber} (seller role)`
        );
      }
      if (bsaleNumber === "10475") {
        console.log(
          `❌ CREDIT NOTE 10475 EXCLUDED: Final exclusion (no matching conditions)`
        );
      }
      return false;
    });

    // Debug final results for sellers
    if (userRole === "seller") {
      console.log(
        `🔍 EXPORT FILTER FINAL RESULTS: ${filteredFacturas.length} documents passed filter`
      );
      const creditNotes = filteredFacturas.filter(
        (f) => f.documentType === "nota_de_credito"
      );
      const bsaleDirectos = filteredFacturas.filter(
        (f) => f.isTemporary === true
      );
      console.log(
        `🔍 EXPORT FILTER FINAL: Credit notes passed: ${creditNotes.length}`
      );
      console.log(
        `🔍 EXPORT FILTER FINAL: BSale Directo docs passed: ${bsaleDirectos.length}`
      );

      if (bsaleDirectos.length > 0) {
        console.log(
          `⚠️ BSale Directo docs that passed filter:`,
          bsaleDirectos.map((f) => ({
            bsale_number: (f as any).bsale_number,
            documentType: f.documentType,
            isTemporary: f.isTemporary,
          }))
        );
      }
    }

    // Check if target credit notes survived filtering
    const survivingTargetCreditNotes = filteredFacturas.filter(
      (f) =>
        f.documentType === "nota_de_credito" &&
        ["10636", "10637"].includes((f as any).bsale_number?.toString())
    );
    console.log(
      `🔍 Excel Export - Target credit notes after filtering: ${survivingTargetCreditNotes.length}`
    );
    if (survivingTargetCreditNotes.length > 0) {
      console.log(
        `🔍 Excel Export - Surviving target credit notes:`,
        survivingTargetCreditNotes.map((cn) => (cn as any).bsale_number)
      );
    } else {
      console.log(
        `❌ Excel Export - Target credit notes 10636 and 10637 were filtered out!`
      );
    }

    // Create a map to track which sales_request_ids have both factura and credit note
    // to prevent double-counting when both documents share the same sales_request_id
    const salesRequestWithBothTypes = new Map<
      string,
      { factura: any; creditNote: any }
    >();

    filteredFacturas.forEach((factura) => {
      if (factura.salesRequestId) {
        const existing = salesRequestWithBothTypes.get(factura.salesRequestId);
        if (existing) {
          if (factura.documentType === "nota_de_credito") {
            existing.creditNote = factura;
          } else {
            existing.factura = factura;
          }
        } else {
          salesRequestWithBothTypes.set(factura.salesRequestId, {
            factura:
              factura.documentType === "nota_de_credito" ? null : factura,
            creditNote:
              factura.documentType === "nota_de_credito" ? factura : null,
          });
        }
      }
    });

    // First pass: identify documents that need client info from BSale API
    const missingClientDocuments: Array<{
      factura: any;
      clientId: string;
    }> = [];

    // Get bsale numbers for credit notes that need client info
    const creditNotesToCheck = filteredFacturas
      .filter(
        (f) =>
          !f.salesRequestId &&
          f.documentType === "nota_de_credito" &&
          (f as any).bsale_number
      )
      .map((f) => (f as any).bsale_number);

    // Fetch raw database documents with response_data for client extraction
    let rawDatabaseDocs: any[] = [];
    if (creditNotesToCheck.length > 0) {
      const { data: rawDocs, error: rawDocsError } = await supabase
        .from("sales_request_documents")
        .select("*")
        .in("bsale_number", creditNotesToCheck)
        .eq("document_type", "nota_de_credito");

      if (!rawDocsError) {
        rawDatabaseDocs = rawDocs || [];
      }
    }

    console.log("🔍 REACHING CREDIT NOTE SECTION");

    // Also fetch ALL credit notes with their credit note specific fields to ensure we have the data
    const allCreditNotes = filteredFacturas.filter(
      (f) => f.documentType === "nota_de_credito"
    );

    console.log(
      `🔍 FOUND ${allCreditNotes.length} CREDIT NOTES:`,
      allCreditNotes.map((cn) => (cn as any).bsale_number)
    );
    const creditNoteBsaleNumbers = allCreditNotes
      .map((f) => (f as any).bsale_number)
      .filter(Boolean);

    let enrichedCreditNoteData = new Map<string, any>();
    if (creditNoteBsaleNumbers.length > 0) {
      console.log(
        `🔍 MOTIVE DEBUG: Looking up ${creditNoteBsaleNumbers.length} credit notes:`,
        creditNoteBsaleNumbers
      );

      const { data: creditNoteDetails, error: creditNoteError } = await (
        supabase as any
      )
        .from("sales_request_documents")
        .select(
          "bsale_number, motive, administrative_reason, reference, response_data"
        )
        .in("bsale_number", creditNoteBsaleNumbers)
        .eq("document_type", "nota_de_credito");

      console.log(`🔍 MOTIVE DEBUG: Query result:`, {
        creditNoteDetails,
        creditNoteError,
      });

      if (!creditNoteError && creditNoteDetails) {
        enrichedCreditNoteData = new Map(
          creditNoteDetails.map((cn: any) => [
            cn.bsale_number?.toString() || "",
            cn,
          ])
        );
        console.log(
          `🔍 MOTIVE DEBUG: Created enrichedCreditNoteData map with ${enrichedCreditNoteData.size} entries`
        );

        // Log each entry in the map
        enrichedCreditNoteData.forEach((value, key) => {
          console.log(`🔍 MOTIVE DEBUG: Map entry [${key}]:`, {
            motive: value.motive,
            administrative_reason: value.administrative_reason,
          });
        });
      } else {
        console.error(`🔍 MOTIVE DEBUG: Error or no data:`, creditNoteError);
      }
    } else {
      console.log(`🔍 MOTIVE DEBUG: No credit note bsale numbers found`);
    }

    // Collect all reference_document.id values and reference_document.number values that we need to look up
    const referenceIdsToLookup = new Set<string>();
    const referenceNumbersToLookup = new Set<string>();

    enrichedCreditNoteData.forEach((creditNoteData) => {
      if (creditNoteData.response_data?.reference_document?.id) {
        referenceIdsToLookup.add(
          creditNoteData.response_data.reference_document.id.toString()
        );
      }
      if (creditNoteData.response_data?.reference_document?.number) {
        referenceNumbersToLookup.add(
          creditNoteData.response_data.reference_document.number.toString()
        );
      }
    });

    // Also check response_data from the facturas directly
    filteredFacturas.forEach((factura) => {
      if (factura.documentType === "nota_de_credito") {
        const responseData = (factura as any).response_data;
        if (responseData?.reference_document?.id) {
          referenceIdsToLookup.add(
            responseData.reference_document.id.toString()
          );
        }
        if (responseData?.reference_document?.number) {
          referenceNumbersToLookup.add(
            responseData.reference_document.number.toString()
          );
        }
      }
    });

    console.log(
      `🔍 Found ${referenceIdsToLookup.size} reference document IDs to lookup:`,
      Array.from(referenceIdsToLookup)
    );
    console.log(
      `🔍 Found ${referenceNumbersToLookup.size} reference document numbers to lookup:`,
      Array.from(referenceNumbersToLookup)
    );

    // Query database for facturas with these external_ids
    let originalFacturasLookupMap = new Map<string, any>();
    let originalFacturasByNumberMap = new Map<string, any>();

    if (referenceIdsToLookup.size > 0) {
      const { data: originalFacturasFromDb, error: originalFacturasDbError } =
        await supabase
          .from("sales_request_documents")
          .select("external_id, bsale_number, created_at")
          .in(
            "external_id",
            Array.from(referenceIdsToLookup)
              .map((id) => parseInt(id))
              .filter((id) => !isNaN(id))
          )
          .eq("document_type", "factura");

      if (!originalFacturasDbError && originalFacturasFromDb) {
        originalFacturasLookupMap = new Map(
          originalFacturasFromDb.map((f) => [
            f.external_id?.toString() || "",
            f,
          ])
        );
        console.log(
          `✅ Found ${originalFacturasFromDb.length} original facturas by external_id:`,
          originalFacturasFromDb.map(
            (f) => `${f.external_id} -> ${f.bsale_number}`
          )
        );
      } else {
        console.log(
          `⚠️ Error or no original facturas found by external_id:`,
          originalFacturasDbError
        );
      }
    }

    // Also query by bsale_number as fallback (for facturas without external_id)
    if (referenceNumbersToLookup.size > 0) {
      const { data: originalFacturasByNumber, error: facturasByNumberError } =
        await supabase
          .from("sales_request_documents")
          .select("bsale_number, created_at")
          .in("bsale_number", Array.from(referenceNumbersToLookup))
          .eq("document_type", "factura");

      if (!facturasByNumberError && originalFacturasByNumber) {
        originalFacturasByNumberMap = new Map(
          originalFacturasByNumber.map((f) => [
            f.bsale_number?.toString() || "",
            f,
          ])
        );
        console.log(
          `✅ Found ${originalFacturasByNumber.length} original facturas by bsale_number:`,
          originalFacturasByNumber.map((f) => f.bsale_number)
        );
      } else {
        console.log(
          `⚠️ Error or no original facturas found by bsale_number:`,
          facturasByNumberError
        );
      }
    }

    for (const factura of filteredFacturas) {
      const hasClientInMap =
        factura.clientId && clientMap.get(factura.clientId);
      const hasClientInResponseData =
        !factura.salesRequestId &&
        (factura as any).response_data &&
        ((factura as any).response_data.client ||
          (factura as any).response_data.credit_note?.client ||
          (factura as any).response_data.reference_document?.client);

      if (
        !hasClientInMap &&
        !hasClientInResponseData &&
        (factura as any).bsale_number
      ) {
        const clientId = extractClientIdFromBSaleDocument(
          String((factura as any).bsale_number),
          rawDatabaseDocs
        );

        if (clientId) {
          missingClientDocuments.push({ factura, clientId });
        }
      }
    }

    // Fetch missing client information from BSale API
    const additionalClients = new Map<string, any>();
    for (const { factura, clientId } of missingClientDocuments) {
      try {
        let bsaleClient = null;

        // Check if this is a reference document marker
        if (clientId.startsWith("REF_DOC:")) {
          const originalDocId = clientId.replace("REF_DOC:", "");

          // Fetch the original document to get client info
          const originalDocResponse = await bsaleFetch(
            `/documents/${originalDocId}.json`
          );

          if (originalDocResponse.ok) {
            const originalDoc = await originalDocResponse.json();

            if (originalDoc.client?.id) {
              bsaleClient = await fetchBSaleClientInfo(originalDoc.client.id);
            }
          }
        } else {
          // Direct client ID
          bsaleClient = await fetchBSaleClientInfo(clientId);
        }

        if (bsaleClient) {
          additionalClients.set(
            String((factura as any).bsale_number),
            bsaleClient
          );
        }
      } catch (error) {
        // Silently handle errors
      }
    }

    // 🔍 BEFORE PROCESSING: Check what documents we have
    const creditNotesCount = filteredFacturas.filter(
      (f) =>
        (f as any).document_type === "nota_de_credito" ||
        f.documentType === "nota_de_credito"
    ).length;
    console.log(
      `🔍 BEFORE PROCESSING: Total filteredFacturas=${filteredFacturas.length}, Credit Notes=${creditNotesCount}`
    );

    // Process each factura
    const exportData: FacturaExportData[] = filteredFacturas.map((factura) => {
      // 🔍 VERY EARLY LOGGING: Check what type of document we're processing
      const debugDocumentType =
        (factura as any).document_type || factura.documentType;
      const debugBsaleNumber =
        (factura as any).bsale_number?.toString() || "unknown";
      console.log(
        `🔍 PROCESSING DOCUMENT: Type="${debugDocumentType}", BSale="${debugBsaleNumber}"`
      );

      // Get bsale_number from database field first, then fall back to filename extraction
      let folioFactura = "N/A";

      // Check if we have bsale_number in the factura object (from database)
      if ((factura as any).bsale_number) {
        folioFactura = String((factura as any).bsale_number);

        // Add "-NC" suffix for credit notes
        const documentType = factura.documentType;
        if (documentType === "nota_de_credito") {
          folioFactura += "-NC";
        }
      } else {
        // Fall back to extracting from filename
        const fileName = factura.fileName || "";
        const bsaleNumberMatch = fileName.match(/factura-(\d+)\.pdf/);
        folioFactura = bsaleNumberMatch ? bsaleNumberMatch[1] : "N/A";
      }

      // Get sales request data
      const salesRequest = factura.salesRequestId
        ? salesRequestMap.get(factura.salesRequestId)
        : null;

      // Get client data - for standalone credit notes, try to get from BSale data
      let client = factura.clientId ? clientMap.get(factura.clientId) : null;

      // For standalone credit notes, try to extract client info from BSale response data
      if (
        !client &&
        !factura.salesRequestId &&
        (factura as any).response_data
      ) {
        const bsaleData = (factura as any).response_data;

        // Try multiple extraction methods for better compatibility
        let clientData = null;

        // Method 1: Direct client object
        if (bsaleData.client) {
          clientData = bsaleData.client;
        }

        // Method 2: Credit note client object
        if (!clientData && bsaleData.credit_note?.client) {
          clientData = bsaleData.credit_note.client;
        }

        // Method 3: Look for client in nested structures
        if (!clientData && bsaleData.reference_document?.client) {
          clientData = bsaleData.reference_document.client;
        }

        // Method 4: Look for client in document root (some BSale structures)
        if (!clientData && bsaleData.document?.client) {
          clientData = bsaleData.document.client;
        }

        // Method 5: Search recursively for any client object
        if (!clientData) {
          const findClientInObject = (obj: any): any => {
            if (!obj || typeof obj !== "object") return null;

            // If this object looks like a client (has id and either company/firstName/name)
            if (
              obj.id &&
              (obj.company || obj.firstName || obj.name || obj.razonSocial)
            ) {
              return obj;
            }

            // Search in nested objects
            for (const key in obj) {
              if (
                key === "client" &&
                obj[key] &&
                typeof obj[key] === "object"
              ) {
                return obj[key];
              }
              if (typeof obj[key] === "object" && obj[key] !== null) {
                const found = findClientInObject(obj[key]);
                if (found) return found;
              }
            }
            return null;
          };

          clientData = findClientInObject(bsaleData);
        }

        // Extract client information from found data
        if (clientData) {
          // Try multiple field mappings for client name
          let clientName = "Cliente BSale";
          if (clientData.company) {
            clientName = clientData.company;
          } else if (clientData.firstName && clientData.lastName) {
            clientName =
              `${clientData.firstName} ${clientData.lastName}`.trim();
          } else if (clientData.firstName) {
            clientName = clientData.firstName;
          } else if (clientData.name) {
            clientName = clientData.name;
          } else if (clientData.razonSocial) {
            clientName = clientData.razonSocial;
          }

          // Try multiple field mappings for tax ID
          let taxId = "N/A";
          if (clientData.code) {
            taxId = clientData.code;
          } else if (clientData.rut) {
            taxId = clientData.rut;
          } else if (clientData.rutBsale) {
            taxId = clientData.rutBsale;
          } else if (clientData.taxId) {
            taxId = clientData.taxId;
          }

          client = {
            id: clientData.id,
            name: clientName,
            tax_id: taxId,
            sales_conditions: null,
          };

          console.log(
            `✅ Extracted client info for credit note ${(factura as any).bsale_number}:`,
            {
              name: client.name,
              tax_id: client.tax_id,
              originalClientData: clientData,
            }
          );
        } else {
          console.log(
            `⚠️ No client data found in response_data for credit note ${(factura as any).bsale_number}`
          );
          console.log(
            `   Full response_data:`,
            JSON.stringify(bsaleData, null, 2)
          );
        }
      }

      // Debug logging for factura 236949 or matching sales requests
      const bsaleNumber = String((factura as any).bsale_number || "");

      // If client is still not found, check if we have it in additionalClients
      if (!client && (factura as any).bsale_number) {
        client = additionalClients.get(bsaleNumber) || null;
      }

      // Determine payment condition - prioritize sales request's condition over client's current condition
      let condicionPago = "N/A";

      // For facturas, prioritize the sales request's condition over client's current condition
      // This ensures invoices reflect the payment terms that were agreed upon when the sales request was created
      if (salesRequest?.sales_conditions?.name) {
        // Use sales request's condition (correct for invoices)
        condicionPago = salesRequest.sales_conditions.name;
      } else if (client?.sales_conditions?.name) {
        // Fall back to client's current condition if sales request has none
        condicionPago = client.sales_conditions.name;
      }

      // Calculate expiration date
      let fechaExpiracion = "N/A";

      // Credit notes expire one day after their generation date
      if (factura.documentType === "nota_de_credito") {
        if (factura.createdAt) {
          try {
            const creationDate = new Date(factura.createdAt);
            if (!isNaN(creationDate.getTime())) {
              // Set expiration to one day after creation date
              const expirationDate = new Date(creationDate);
              expirationDate.setDate(expirationDate.getDate() + 1);
              fechaExpiracion = format(expirationDate, "dd/MM/yyyy", {
                locale: es,
              });
            }
          } catch (error) {
            console.error(
              "Error calculating expiration for credit note:",
              error
            );
          }
        }
      } else {
        // For regular facturas, use existing logic
        // Try to calculate correct expiration based on sales condition and factura creation date
        let correctExpirationCalculated = false;

        // Determine which sales condition to use (prioritize sales request's historical condition over client's current)
        const conditionToUse =
          salesRequest?.sales_conditions || client?.sales_conditions;

        if (
          conditionToUse?.maximum_days !== undefined &&
          salesRequest?.factura_creation_date
        ) {
          try {
            const facturaCreationDate = new Date(
              salesRequest.factura_creation_date
            );
            if (!isNaN(facturaCreationDate.getTime())) {
              // Special case: anticipada conditions mean immediate payment (expiration = creation date)
              const isAnticipadaCondition = conditionToUse?.name
                ?.toLowerCase()
                .includes("anticipada");

              if (isAnticipadaCondition) {
                fechaExpiracion = format(facturaCreationDate, "dd/MM/yyyy", {
                  locale: es,
                });
              } else {
                const maxDays = conditionToUse.maximum_days || 0;
                const expirationDate = new Date(facturaCreationDate);
                expirationDate.setDate(expirationDate.getDate() + maxDays);
                fechaExpiracion = format(expirationDate, "dd/MM/yyyy", {
                  locale: es,
                });
              }
              correctExpirationCalculated = true;
            }
          } catch (error) {
            console.error(
              "Error calculating expiration from sales condition:",
              error
            );
          }
        }

        // Fallback to payment_due_date if we couldn't calculate from sales condition
        if (!correctExpirationCalculated && salesRequest?.payment_due_date) {
          try {
            const dueDate = new Date(salesRequest.payment_due_date);
            if (!isNaN(dueDate.getTime())) {
              fechaExpiracion = format(dueDate, "dd/MM/yyyy", { locale: es });
            }
          } catch (error) {
            console.error("Error formatting due date:", error);
          }
        }
      }

      // Get amounts - use document's own amounts for credit notes
      const documentType = factura.documentType;

      // For all facturas, prefer net_amount from sales_request_documents (via optimized view)
      // This ensures we show BSale PDF values, not discounted values from sales_request
      const docNetAmount = parseFloat((factura as any).net_amount) || 0;
      const docTaxAmount = parseFloat((factura as any).tax_amount) || 0;

      // Use document amounts if available, otherwise fall back to sales_request
      let montoNeto =
        docNetAmount > 0
          ? Math.round(docNetAmount)
          : Math.round(salesRequest?.total_amount || 0);
      let montoBruto =
        docNetAmount > 0
          ? Math.round(docNetAmount + docTaxAmount)
          : grossFromNet(salesRequest?.total_amount, salesRequest?.is_exento);

      // For credit notes, use the document's net amount and calculate gross amount
      if (documentType === "nota_de_credito") {
        let docNetAmount = parseFloat((factura as any).net_amount) || 0;
        let docTaxAmount = parseFloat((factura as any).tax_amount) || 0;
        const docTotalAmount =
          parseFloat((factura as any).document_total_amount) || 0;

        // Debug credit notes that might have zero tax
        const creditNoteBsale = (factura as any).bsale_number?.toString();
        if (["10573", "10574"].includes(creditNoteBsale)) {
          console.log(`🔍 Debug credit note ${creditNoteBsale}:`, {
            originalNetAmount: (factura as any).net_amount,
            originalTaxAmount: (factura as any).tax_amount,
            originalTotalAmount: (factura as any).document_total_amount,
            parsedNetAmount: docNetAmount,
            parsedTaxAmount: docTaxAmount,
            parsedTotalAmount: docTotalAmount,
          });
        }

        // ALL credit notes should be negative (they represent revenue reductions)
        docNetAmount = -Math.abs(docNetAmount); // Make negative
        docTaxAmount = -Math.abs(docTaxAmount); // Make negative

        // If tax_amount is 0 but we have a total_amount, try to calculate tax from the difference
        if (docTaxAmount === 0 && docTotalAmount > 0) {
          const calculatedTax = -(
            Math.abs(docTotalAmount) - Math.abs(docNetAmount)
          );
          docTaxAmount = calculatedTax;
          console.log(
            `💡 Credit note ${creditNoteBsale}: Calculated tax from total - net:`,
            {
              docNetAmount,
              docTotalAmount: -Math.abs(docTotalAmount),
              calculatedTax,
            }
          );
        }

        montoNeto = Math.round(docNetAmount); // Rounded
        montoBruto = Math.round(docNetAmount + docTaxAmount);

        // Debug the final result for problematic credit notes
        if (["10573", "10574"].includes(creditNoteBsale)) {
          console.log(`📊 Final amounts for credit note ${creditNoteBsale}:`, {
            montoNeto,
            montoBruto,
            docTaxAmount,
            areEqual: montoNeto === montoBruto,
          });
        }
      }

      // For debit notes, use the document's net amount and calculate gross amount (positive amounts)
      if (documentType === "nota_de_debito") {
        let docNetAmount = parseFloat((factura as any).net_amount) || 0;
        let docTaxAmount = parseFloat((factura as any).tax_amount) || 0;

        // Debit notes should be positive (they represent additional charges/corrections)
        docNetAmount = Math.abs(docNetAmount); // Ensure positive
        docTaxAmount = Math.abs(docTaxAmount); // Ensure positive

        montoNeto = Math.round(docNetAmount); // Rounded
        montoBruto = Math.round(docNetAmount + docTaxAmount);
      }

      // Use the corrected paid_amount from the factura data (calculated in fetchAllFacturas)
      // This amount already excludes checks in portfolio
      let totalPagado = 0;

      if (
        documentType === "nota_de_credito" ||
        documentType === "nota_de_debito"
      ) {
        // Credit notes and debit notes always have 0 payment
        totalPagado = 0;
      } else {
        // Enhanced paid amount calculation will be done after payment status determination
        totalPagado = (factura as any).paid_amount || 0;
      }

      // Get payment date (latest payment date)
      let fechaPago = "N/A";
      if (
        factura.salesRequestId &&
        paymentDatesMap.has(factura.salesRequestId)
      ) {
        const paymentDate = paymentDatesMap.get(factura.salesRequestId);
        if (paymentDate) {
          fechaPago = format(paymentDate, "dd/MM/yyyy", { locale: es });
        }
      }

      // Get seller name
      const nombreVendedor = salesRequest?.profiles
        ? `${salesRequest.profiles.name || ""} ${salesRequest.profiles.lastname || ""}`.trim()
        : factura.isTemporary === true
          ? "BSale Directo"
          : "N/A";

      // Determine payment status using the sales request's paid status
      // First try the paid status embedded in the factura object (from fetchAllFacturas)
      // If not available, fall back to the salesRequest lookup

      // Debug logging for specific problematic facturas
      const isProblematicFactura = ["236780", "236441", "238998"].includes(
        bsaleNumber
      );
      if (isProblematicFactura) {
        console.log(`🔍 PAYMENT DEBUG - Factura ${bsaleNumber}:`, {
          sales_request_paid: (factura as any).sales_request_paid,
          sales_request_paid_type: typeof (factura as any).sales_request_paid,
          salesRequest_exists: !!salesRequest,
          salesRequest_paid: salesRequest?.paid,
          salesRequest_paid_type: typeof salesRequest?.paid,
          salesRequestId: factura.salesRequestId,
          paid_amount_factura: (factura as any).paid_amount,
          paid_amount_salesRequest: salesRequest?.paid_amount,
        });
      }

      // Enhanced payment status logic that considers multiple sources
      let paidStatus = false;

      // Primary: Use optimized view data
      if ((factura as any).sales_request_paid !== undefined) {
        paidStatus = (factura as any).sales_request_paid;
      }
      // Fallback 1: Use sales request lookup data
      else if (salesRequest?.paid !== undefined) {
        paidStatus = salesRequest.paid;
      }
      // Fallback 2: Calculate based on paid amount vs total amount
      else {
        const currentPaidAmount =
          (factura as any).paid_amount || salesRequest?.paid_amount || 0;
        const totalAmount =
          (factura as any).price_with_iva ||
          salesRequest?.price_with_iva ||
          (factura as any).total_amount ||
          salesRequest?.total_amount ||
          0;
        paidStatus = currentPaidAmount >= totalAmount && totalAmount > 0;
      }

      const statusPago = paidStatus === true ? "PAGADO" : "PENDIENTE";

      // Enhanced paid amount calculation for facturas
      // If the sales request is marked as paid but paid_amount is 0,
      // it means it was paid via credit notes, so show the total amount as paid
      if (documentType === "factura" && paidStatus && totalPagado === 0) {
        // Payment via credit notes - show total amount as paid for display
        const totalAmount =
          (factura as any).price_with_iva ||
          salesRequest?.price_with_iva ||
          (factura as any).total_amount ||
          salesRequest?.total_amount ||
          0;
        totalPagado = totalAmount;
      }

      if (isProblematicFactura) {
        console.log(
          `🔍 PAYMENT RESULT - Factura ${bsaleNumber}: ${statusPago}`,
          {
            finalPaidStatus: paidStatus,
            paidAmount:
              (factura as any).paid_amount || salesRequest?.paid_amount || 0,
            enhancedTotalPagado: totalPagado,
            totalAmount:
              (factura as any).price_with_iva ||
              salesRequest?.price_with_iva ||
              0,
          }
        );
      }

      // Check if this factura is cancelled
      const isCancelled = (factura as any).cancelled === true;

      // Credit note specific data
      let originalFacturaNumber = "";
      let facturaCreationDate = "";
      let cancellationReason = "";
      console.log("checking document type:", factura.documentType);
      if (documentType === "nota_de_credito") {
        // Get enriched credit note data
        const enrichedData = enrichedCreditNoteData.get(bsaleNumber);

        // 🔍 EXCEL EXPORT DEBUG: Log initial credit note processing
        console.log(`🔍 EXCEL EXPORT - Processing Credit Note ${bsaleNumber}:`);
        console.log(
          `   - enrichedData available: ${enrichedData ? "YES" : "NO"}`
        );
        if (enrichedData) {
          console.log(`   - enrichedData keys:`, Object.keys(enrichedData));
          console.log(
            `   - enrichedData.motive: "${enrichedData.motive || "N/A"}"`
          );
          console.log(
            `   - enrichedData.administrative_reason: "${enrichedData.administrative_reason || "N/A"}"`
          );
          console.log(
            `   - enrichedData.original_document_id: "${enrichedData.original_document_id || "N/A"}"`
          );
        }
        console.log(
          `   - factura.motive: "${(factura as any).motive || "N/A"}"`
        );
        console.log(
          `   - factura.administrative_reason: "${(factura as any).administrative_reason || "N/A"}"`
        );
        console.log(
          `   - factura.original_document_id: "${(factura as any).original_document_id || "N/A"}"`
        );
        console.log(
          `   - factura.response_data available: ${(factura as any).response_data ? "YES" : "NO"}`
        );
        if ((factura as any).response_data?.reference_document) {
          console.log(
            `   - factura.response_data.reference_document:`,
            (factura as any).response_data.reference_document
          );
        }

        // Method 1: Try original_document_id from enriched data first
        const originalDocumentId =
          enrichedData?.original_document_id ||
          (factura as any).original_document_id;
        if (originalDocumentId) {
          const originalFactura = originalFacturasMap.get(originalDocumentId);

          if (originalFactura) {
            originalFacturaNumber =
              originalFactura.bsale_number?.toString() || "N/A";
            facturaCreationDate = originalFactura.created_at
              ? format(new Date(originalFactura.created_at), "dd/MM/yyyy", {
                  locale: es,
                })
              : "N/A";
          }
        }

        // Method 2: Try to extract from BSale response data if no original_document_id
        if (!originalFacturaNumber || originalFacturaNumber === "N/A") {
          const responseData =
            enrichedData?.response_data || (factura as any).response_data;

          if (responseData?.reference_document) {
            // Extract factura number from reference document
            const refDoc = responseData.reference_document;
            if (refDoc.number) {
              originalFacturaNumber = refDoc.number.toString();
            }
            if (refDoc.emissionDate) {
              try {
                facturaCreationDate = format(
                  new Date(refDoc.emissionDate),
                  "dd/MM/yyyy",
                  {
                    locale: es,
                  }
                );
              } catch (e) {
                console.error("Error parsing emission date:", e);
              }
            }
          }
        }

        // Method 3: Try to extract from reference field (BSale href)
        if (!originalFacturaNumber || originalFacturaNumber === "N/A") {
          const referenceUrl =
            enrichedData?.reference || (factura as any).reference;
          if (referenceUrl) {
            try {
              // The reference field might contain a BSale API URL like:
              // https://api.bsale.io/v1/documents/12345.json
              const documentIdMatch = referenceUrl.match(
                /documents\/(\d+)\.json/
              );
              if (documentIdMatch) {
                const bsaleDocumentId = documentIdMatch[1];
                // Try to find this BSale document in our current facturas list
                const matchingFactura = filteredFacturas.find(
                  (f) =>
                    (f as any).bsale_number?.toString() === bsaleDocumentId ||
                    (f as any).external_id?.toString() === bsaleDocumentId
                );

                if (matchingFactura) {
                  originalFacturaNumber = bsaleDocumentId;
                  if (matchingFactura.createdAt) {
                    facturaCreationDate = format(
                      new Date(matchingFactura.createdAt),
                      "dd/MM/yyyy",
                      {
                        locale: es,
                      }
                    );
                  }
                }
              }
            } catch (e) {
              console.error("Error parsing reference URL:", e);
            }
          }
        }

        // Method 4: Extract from BSale response_data using reference_document.id
        if (!originalFacturaNumber || originalFacturaNumber === "N/A") {
          const responseData =
            enrichedData?.response_data || (factura as any).response_data;

          if (responseData?.reference_document?.id) {
            const referenceDocumentId =
              responseData.reference_document.id.toString();

            // First try to find in current filtered facturas
            const matchingFactura = filteredFacturas.find(
              (f) => (f as any).external_id?.toString() === referenceDocumentId
            );

            // If not found in filtered facturas, check our database lookup
            if (!matchingFactura) {
              const facturaFromDb =
                originalFacturasLookupMap.get(referenceDocumentId);
              if (facturaFromDb) {
                originalFacturaNumber =
                  facturaFromDb.bsale_number?.toString() || "N/A";
                facturaCreationDate = facturaFromDb.created_at
                  ? format(new Date(facturaFromDb.created_at), "dd/MM/yyyy", {
                      locale: es,
                    })
                  : "N/A";
              } else {
                console.log(
                  `⚠️ Method 4a - No factura found with external_id ${referenceDocumentId}`
                );

                // Method 4b: Fallback to lookup by reference_document.number
                if (responseData?.reference_document?.number) {
                  const referenceDocumentNumber =
                    responseData.reference_document.number.toString();
                  const facturaByNumber = originalFacturasByNumberMap.get(
                    referenceDocumentNumber
                  );

                  if (facturaByNumber) {
                    originalFacturaNumber =
                      facturaByNumber.bsale_number?.toString() || "N/A";
                    facturaCreationDate = facturaByNumber.created_at
                      ? format(
                          new Date(facturaByNumber.created_at),
                          "dd/MM/yyyy",
                          {
                            locale: es,
                          }
                        )
                      : "N/A";
                  }
                }
              }
            } else {
              originalFacturaNumber =
                (matchingFactura as any).bsale_number?.toString() || "N/A";
              facturaCreationDate = matchingFactura.createdAt
                ? format(new Date(matchingFactura.createdAt), "dd/MM/yyyy", {
                    locale: es,
                  })
                : "N/A";
              console.log(
                `✅ Method 4 - Found in filtered facturas: external_id ${referenceDocumentId} -> factura ${originalFacturaNumber}`
              );
            }
          } else {
            console.log(
              `⚠️ Method 4 - No reference_document.id found in response_data for credit note ${bsaleNumber}`
            );
          }
        }

        // Method 5: Special case for credit notes with same sales_request_id as a factura
        if (
          (!originalFacturaNumber || originalFacturaNumber === "N/A") &&
          factura.salesRequestId
        ) {
          // Look for a factura with the same sales_request_id
          const relatedFactura = filteredFacturas.find(
            (f) =>
              f.documentType === "factura" &&
              f.salesRequestId === factura.salesRequestId
          );

          if (relatedFactura) {
            originalFacturaNumber =
              (relatedFactura as any).bsale_number?.toString() || "N/A";
            facturaCreationDate = relatedFactura.createdAt
              ? format(new Date(relatedFactura.createdAt), "dd/MM/yyyy", {
                  locale: es,
                })
              : "N/A";
            console.log(
              `✅ Method 5 - Found related factura by sales_request_id: ${factura.salesRequestId} -> factura ${originalFacturaNumber}`
            );
          }
        }

        // Debug logging for problematic credit notes
        const debugBsaleNumber = (factura as any).bsale_number?.toString();
        if (["10749", "10748", "10747"].includes(debugBsaleNumber)) {
          console.log(`🔍 DEBUG Credit Note ${debugBsaleNumber}:`);
          console.log(
            `   - Final originalFacturaNumber: "${originalFacturaNumber}"`
          );
          console.log(
            `   - enrichedData: ${enrichedData ? "Present" : "Missing"}`
          );
          if (enrichedData) {
            console.log(
              `   - enrichedData.original_document_id: ${enrichedData.original_document_id || "N/A"}`
            );
          }
          console.log(
            `   - factura.original_document_id: ${(factura as any).original_document_id || "N/A"}`
          );
          console.log(
            `   - factura.salesRequestId: ${factura.salesRequestId || "N/A"}`
          );
        }

        // Get cancellation reason from enriched data first, then fallback
        cancellationReason =
          enrichedData?.motive ||
          enrichedData?.administrative_reason ||
          (factura as any).motive ||
          (factura as any).administrative_reason ||
          "No especificado";

        // Debug logging for cancellation reason
        if (["10596", "10598", "10597", "10479"].includes(bsaleNumber)) {
          console.log(`🔍 DEBUG Cancellation Reason for ${bsaleNumber}:`);
          console.log(`  - enrichedData found: ${enrichedData ? "Yes" : "No"}`);
          if (enrichedData) {
            console.log(
              `  - enrichedData.motive: "${enrichedData.motive || "N/A"}"`
            );
            console.log(
              `  - enrichedData.administrative_reason: "${enrichedData.administrative_reason || "N/A"}"`
            );
          }
          console.log(
            `  - factura.motive: "${(factura as any).motive || "N/A"}"`
          );
          console.log(
            `  - factura.administrative_reason: "${(factura as any).administrative_reason || "N/A"}"`
          );
          console.log(`  - Final cancellationReason: "${cancellationReason}"`);
        }
      }

      // Convert document type to Spanish
      const getDocumentTypeInSpanish = (type: string): string => {
        switch (type) {
          case "factura":
            return "Factura";
          case "nota_de_credito":
            return "Nota de Crédito";
          case "nota_de_debito":
            return "Nota de Débito";
          default:
            return "Documento";
        }
      };

      // Get previous seller for this client
      const vendedorAnterior = factura.clientId
        ? previousSellersMap.get(factura.clientId) || "Sin vendedor anterior"
        : "Sin vendedor anterior";

      // Get previous seller change date for this client
      const fechaCambioVendedor = factura.clientId
        ? sellerChangeDatesMap.get(factura.clientId) || "Sin fecha"
        : "Sin fecha";

      return {
        folioFactura: isCancelled ? `${folioFactura} (ANULADO)` : folioFactura,
        tipoDocumento: getDocumentTypeInSpanish(documentType),
        idNotaVenta:
          factura.salesRequestId ||
          (factura.isTemporary === true ? "BSale Directo" : "N/A"),
        nombreCliente: client?.name || "Cliente no encontrado",
        rutCliente: client?.tax_id || "N/A",
        fechaEmision: factura.createdAt
          ? format(new Date(factura.createdAt), "dd/MM/yyyy", { locale: es })
          : "N/A",
        condicionPago,
        fechaExpiracion,
        montoNeto,
        montoBruto,
        totalPagado,
        fechaPago,
        statusPago: isCancelled ? "ANULADO" : statusPago,
        nombreVendedor,
        vendedorAnterior,
        fechaCambioVendedor,
        // Credit note specific columns
        originalFacturaNumber:
          documentType === "nota_de_credito"
            ? originalFacturaNumber
            : undefined,
        facturaCreationDate:
          documentType === "nota_de_credito" ? facturaCreationDate : undefined,
        cancellationReason:
          documentType === "nota_de_credito" ? cancellationReason : undefined,
        // Client status columns
        siniestro: client?.siniestro || false,
        cobranzaJudicial: client?.cobranza_judicial || false,
        cancelled: isCancelled,
      };

      // 🔍 EXCEL EXPORT DEBUG: Log credit note column values
      if (documentType === "nota_de_credito") {
        console.log(
          `🔍 EXCEL EXPORT - Credit Note ${bsaleNumber} Final Column Values:`
        );
        console.log(`   - originalFacturaNumber: "${originalFacturaNumber}"`);
        console.log(`   - facturaCreationDate: "${facturaCreationDate}"`);
        console.log(`   - cancellationReason: "${cancellationReason}"`);
        console.log(
          `   - Will show in Excel as: "${originalFacturaNumber || ""}", "${facturaCreationDate || ""}", "${cancellationReason || ""}"`
        );
      }

      return exportDataItem;
    });

    // 🔍 EXCEL EXPORT DEBUG: Summary of credit note processing
    const creditNotesInExport = exportData.filter(
      (item) => item.tipoDocumento === "Nota de Crédito"
    );
    console.log(
      `🔍 EXCEL EXPORT SUMMARY - Total credit notes in export: ${creditNotesInExport.length}`
    );
    creditNotesInExport.forEach((item, index) => {
      console.log(
        `   ${index + 1}. BSale: ${item.nroFactura.replace("-NC", "")} | Original: "${item.originalFacturaNumber || "BLANK"}" | Creation: "${item.facturaCreationDate || "BLANK"}" | Motive: "${item.cancellationReason || "BLANK"}"`
      );
    });

    return exportData;
  } catch (error) {
    console.error("Error in fetchFacturasForExport:", error);
    if (error instanceof Error) {
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        name: error.name,
      });
    }
  }
};

/**
 * Determines if the date range represents a complete month
 */
const isCompleteMonthView = (startDate?: Date, endDate?: Date): boolean => {
  if (!startDate || !endDate) return false;

  const startOfSelectedMonth = startOfMonth(startDate);
  const endOfSelectedMonth = endOfMonth(startDate);

  return (
    isSameDay(startOfDay(startDate), startOfSelectedMonth) &&
    isSameDay(startOfDay(endDate), endOfSelectedMonth) &&
    isSameMonth(startDate, endDate)
  );
};

/**
 * Fetches cross-period corrected monthly totals
 */
const fetchMonthlyTotals = async (
  year: number,
  month: number
): Promise<MonthlyTotals | null> => {
  try {
    const { data, error } = await supabase.rpc("get_monthly_invoice_totals", {
      target_year: year,
      target_month: month,
    });

    if (error) {
      console.error("Error fetching monthly totals:", error);
      return null;
    }

    return data?.[0] || null;
  } catch (error) {
    console.error("Error fetching monthly totals:", error);
    return null;
  }
};

/**
 * Exports facturas data to Excel file
 */
export const exportFacturasToExcel = async (
  facturas: SalesRequestDocument[],
  startDate?: Date,
  endDate?: Date,
  userRole?: string,
  currentUserId?: string
): Promise<void> => {
  console.log("🚀 EXCEL EXPORT STARTED - exportFacturasToExcel called");
  console.log("📊 Input data:", {
    facturasCount: facturas.length,
    startDate,
    endDate,
    userRole,
    currentUserId,
  });

  try {
    const has236949 = facturas.some(
      (f) => String((f as any).bsale_number) === "236949"
    );
    console.log("✅ DEBUG: Factura 236949 found in input:", has236949);
    console.log(`🔥 EXCEL EXPORT: Input facturas count: ${facturas.length}`);
    console.log("🔍 STEP A: About to call prepareDataForExport...");

    // Detect if we're filtering for June 2025 (any date range within June 2025)
    const isFilteringJune2025 =
      startDate &&
      endDate &&
      startDate.getFullYear() === 2025 &&
      endDate.getFullYear() === 2025 &&
      startDate.getMonth() === 5 &&
      endDate.getMonth() === 5;

    // Detect if we're filtering any range that includes May 2025
    const isFilteringRangeIncludingMay2025 =
      startDate &&
      endDate &&
      startDate.getFullYear() === 2025 &&
      endDate.getFullYear() === 2025 &&
      startDate.getMonth() <= 4 && // Starts in May or earlier
      endDate.getMonth() >= 4; // Ends in May or later

    // Detect if we're filtering for July 2025 or later (when all documents should be included)
    const isFilteringJuly2025OrLater =
      startDate &&
      endDate &&
      ((startDate.getFullYear() === 2025 && startDate.getMonth() >= 6) ||
        startDate.getFullYear() > 2025);

    // Apply date-based filtering logic for cancelled documents
    let facturasForExport = isFilteringJuly2025OrLater
      ? facturas // Include ALL facturas for July 2025 and later
      : facturas.filter((factura) => {
          const isCancelled = (factura as any).cancelled === true;

          // If not cancelled, always include
          if (!isCancelled) {
            return true;
          }

          // Special case for ranges including May 2025: Include cancelled facturas to match their credit notes
          // This ensures proper accounting when credit notes reference cancelled facturas
          if (isFilteringRangeIncludingMay2025) {
            console.log(
              `Range including May 2025: Including cancelled factura ${(factura as any).bsale_number} to match credit notes`
            );
            return true;
          }

          // Special case for June 2025 only ranges: Include all cancelled facturas to match their credit notes
          // This ensures proper accounting when credit notes reference cancelled facturas
          if (isFilteringJune2025) {
            console.log(
              `June 2025: Including cancelled factura ${(factura as any).bsale_number} to match credit notes`
            );
            return true;
          }

          // If cancelled, check if we should include based on timing (original logic for pre-July 2025)
          const createdAt = factura.createdAt
            ? new Date(factura.createdAt)
            : null;
          const cancellationDate = (factura as any).cancellationDate
            ? new Date((factura as any).cancellationDate)
            : null;

          // If we don't have proper dates, exclude cancelled facturas (safe default)
          if (!createdAt || !cancellationDate) {
            return false;
          }

          // Check if creation and cancellation are in the same month/year
          const creationMonth = createdAt.getMonth();
          const creationYear = createdAt.getFullYear();
          const cancellationMonth = cancellationDate.getMonth();
          const cancellationYear = cancellationDate.getFullYear();

          const cancelledInSameMonth =
            creationMonth === cancellationMonth &&
            creationYear === cancellationYear;

          // Include cancelled facturas ONLY if they were cancelled in a different month
          return !cancelledInSameMonth;
        });

    console.log(
      "🔍 DEBUG: After initial filtering, checking for factura 236949..."
    );
    const afterInitialFilter = facturasForExport.some(
      (f) => String((f as any).bsale_number) === "236949"
    );
    console.log(
      "✅ DEBUG: Factura 236949 after initial filter:",
      afterInitialFilter
    );

    // Filter out soft-deleted documents
    const beforeDeletedFilter = facturasForExport.length;
    facturasForExport = facturasForExport.filter((factura) => {
      if (factura.deleted_at) {
        const bsaleNumber = (factura as any).bsale_number?.toString();
        console.log(
          `🗑️ EXPORT FILTER: Excluding deleted document ${bsaleNumber} (deleted at: ${factura.deleted_at})`
        );
        return false;
      }
      return true;
    });
    console.log(
      `🗑️ DELETED FILTER: Removed ${beforeDeletedFilter - facturasForExport.length} soft-deleted documents`
    );

    // Apply seller filtering to the export data as well
    if (userRole === "seller" && currentUserId) {
      console.log(
        `🔒 EXPORT: Applying additional seller filtering to ${facturasForExport.length} documents`
      );

      // SECURITY: First exclude all credit notes and debit notes for sellers
      const beforeCreditFilter = facturasForExport.length;
      facturasForExport = facturasForExport.filter((factura) => {
        if (factura.documentType !== "factura") {
          const bsaleNumber = (factura as any).bsale_number?.toString();
          console.log(
            `🚫 SELLER EXCEL FILTER: Excluding ${factura.documentType} ${bsaleNumber} - sellers only see facturas`
          );
          return false;
        }
        return true;
      });
      console.log(
        `🔒 CREDIT NOTE FILTER: Removed ${beforeCreditFilter - facturasForExport.length} non-factura documents`
      );

      // Debug: Log all documents before filtering
      const problematicDocs = facturasForExport.filter((f) =>
        [
          "10636",
          "10637",
          "10594",
          "10473",
          "10566",
          "10569",
          "10567",
        ].includes((f as any).bsale_number?.toString())
      );
      if (problematicDocs.length > 0) {
        console.log(
          `🔍 PROBLEMATIC DOCS BEFORE EXPORT FILTER:`,
          problematicDocs.map((f) => ({
            bsale_number: (f as any).bsale_number,
            isTemporary: f.isTemporary,
            salesRequestId: f.salesRequestId,
            documentType: f.documentType,
          }))
        );
      }

      facturasForExport = facturasForExport.filter((factura) => {
        const bsaleNumber = (factura as any).bsale_number?.toString();

        // SECURITY: Sellers should only see facturas, not credit notes or debit notes
        if (userRole === "seller" && factura.documentType !== "factura") {
          console.log(
            `🚫 SELLER BI FILTER: Excluding ${factura.documentType} ${bsaleNumber} - sellers only see facturas`
          );
          return false;
        }

        // Special debugging for problematic documents
        if (["10636", "10637", "10594"].includes(bsaleNumber)) {
          console.log(
            `🔍 PROCESSING PROBLEMATIC DOC ${bsaleNumber}: isTemporary=${factura.isTemporary}, salesRequestId=${factura.salesRequestId}, docType=${factura.documentType}`
          );
        }

        // Exclude BSale Directo documents
        if (factura.isTemporary === true) {
          console.log(
            `🚫 EXPORT: Excluding BSale Directo document ${bsaleNumber}`
          );
          return false;
        }

        // Exclude specific problematic credit notes for sellers (only July 2025 specific ones)
        if (["10636", "10637"].includes(bsaleNumber)) {
          console.log(
            `🚫 EXPORT: Excluding problematic credit note ${bsaleNumber} for seller`
          );
          return false;
        }

        // Only include documents with sales requests (no standalone credit notes)
        if (!factura.salesRequestId) {
          console.log(
            `🚫 EXPORT: Excluding document without sales request ${bsaleNumber}`
          );
          return false;
        }

        return true;
      });
      console.log(
        `🔒 EXPORT: After seller filtering: ${facturasForExport.length} documents remaining`
      );

      // Debug: Check if problematic docs survived
      const survivingProblematic = facturasForExport.filter((f) =>
        ["10636", "10637"].includes((f as any).bsale_number?.toString())
      );
      if (survivingProblematic.length > 0) {
        console.log(
          `⚠️ PROBLEMATIC DOCS THAT SURVIVED EXPORT FILTER:`,
          survivingProblematic.map((f) => ({
            bsale_number: (f as any).bsale_number,
            isTemporary: f.isTemporary,
            salesRequestId: f.salesRequestId,
          }))
        );
      }
    }

    // Add special facturas for ranges including May 2025 if they're not already included
    if (isFilteringRangeIncludingMay2025) {
      const specialBsaleNumbers = ["232104", "231827"];
      const includedBsaleNumbers = new Set(
        facturasForExport
          .map((f) => (f as any).bsale_number?.toString())
          .filter(Boolean)
      );

      let missingSpecialFacturas = facturas.filter((factura) => {
        const bsaleNumber = (factura as any).bsale_number?.toString();
        return (
          specialBsaleNumbers.includes(bsaleNumber) &&
          !includedBsaleNumbers.has(bsaleNumber)
        );
      });

      // Apply seller filtering to special facturas as well
      if (userRole === "seller" && currentUserId) {
        missingSpecialFacturas = missingSpecialFacturas.filter((factura) => {
          // Only include if it has a sales request from this seller
          return factura.salesRequestId && !factura.isTemporary;
        });
        console.log(
          `🔒 EXPORT: Special facturas after seller filtering: ${missingSpecialFacturas.length}`
        );
      }

      if (missingSpecialFacturas.length > 0) {
        facturasForExport.push(...missingSpecialFacturas);
      }
    }

    console.log("🔍 STEP B: About to call enrichFacturasForExport...");
    // Enrich facturas data (no filtering, just add missing fields)
    const exportData = await enrichFacturasForExport(
      facturasForExport,
      startDate,
      endDate,
      userRole,
      currentUserId
    );
    console.log(
      "🔍 STEP C: enrichFacturasForExport completed, got",
      exportData.length,
      "records"
    );

    // Cost & margin are sensitive — sellers never get these columns.
    const showCostMargin = userRole !== "seller";

    // WAC cost (net CLP) per sales_request, for the Costo/Margen columns.
    const costMap = showCostMargin
      ? await facturaCostsService.getCostsBySalesRequest(
          exportData
            .filter((r) => r.tipoDocumento === "Factura" && r.idNotaVenta)
            .map((r) => r.idNotaVenta)
        )
      : new Map();

    // REMOVED: Hard-coded June 2025 correction - using actual calculated values instead
    // This ensures consistency between single-month and multi-month exports

    // Sort exportData: Nota de débito first, then Facturas, then Notas de crédito, then by bsale number
    const sortedExportData =
      exportData?.sort((a, b) => {
        const getDocumentTypePriority = (tipoDocumento: string): number => {
          if (tipoDocumento.toLowerCase().includes("débito")) return 1;
          if (tipoDocumento.toLowerCase().includes("factura")) return 2;
          if (tipoDocumento.toLowerCase().includes("crédito")) return 3;
          return 4; // fallback
        };

        const priorityDiff =
          getDocumentTypePriority(a.tipoDocumento) -
          getDocumentTypePriority(b.tipoDocumento);

        // If same document type, sort by bsale number (numeric sort)
        if (priorityDiff === 0) {
          const extractBsaleNumber = (folioFactura: string): number => {
            // Remove suffixes like "-NC" and "(ANULADO)" and extract numeric part
            const cleanFolio = folioFactura
              .replace(/-NC.*$/, "")
              .replace(/\s*\(ANULADO\).*$/, "");
            const number = parseInt(cleanFolio, 10);
            return isNaN(number) ? 0 : number;
          };

          return (
            extractBsaleNumber(b.folioFactura) -
            extractBsaleNumber(a.folioFactura)
          );
        }

        return priorityDiff;
      }) || [];

    // Create worksheet data with headers (simplified - no summary)
    const worksheetData = [
      [
        "Folio Factura",
        "Tipo de Documento",
        "ID Nota de Venta",
        "Nombre Cliente",
        "RUT Cliente",
        "Fecha Emisión",
        "Condición de Pago",
        "Fecha de Expiración",
        "Monto Neto",
        "Monto Bruto (+ IVA)",
        ...(showCostMargin ? ["Costo Total", "Margen", "% Margen"] : []),
        "Total Pagado",
        "Fecha Pago",
        "Status Pago",
        "Nombre Vendedor",
        "Vendedor Anterior",
        "Factura Original (NC)",
        "Fecha Creación Factura (NC)",
        "Motivo Anulación (NC)",
        "Siniestro",
        "Cobranza Judicial",
      ],
      ...sortedExportData.map((row) => {
        // Cost/margin only for facturas with a costed amount; "" otherwise.
        // Cancelled (fully-annulled) facturas carry $0 cost: their revenue nets
        // with the NC and the goods return to stock, so counting their WAC would
        // leave cost with no reversal (matches the KPI cards and v_ventas_app_bi).
        const cost =
          row.tipoDocumento === "Factura" && !row.cancelled
            ? costMap.get(row.idNotaVenta)
            : undefined;
        const hasCost =
          !!cost && cost.costedLineCount > 0 && cost.costTotal > 0;
        const costoTotal = hasCost ? Math.round(cost!.costTotal) : "";
        const margen = hasCost
          ? Math.round(row.montoNeto - cost!.costTotal)
          : "";
        const margenPct =
          hasCost && row.montoNeto !== 0
            ? Number(
                (
                  ((row.montoNeto - cost!.costTotal) / row.montoNeto) *
                  100
                ).toFixed(1)
              )
            : "";

        return [
          row.folioFactura,
          row.tipoDocumento,
          row.idNotaVenta,
          row.nombreCliente,
          row.rutCliente,
          row.fechaEmision,
          row.condicionPago,
          row.fechaExpiracion,
          row.montoNeto,
          row.montoBruto,
          ...(showCostMargin ? [costoTotal, margen, margenPct] : []),
          row.totalPagado,
          row.fechaPago,
          row.statusPago,
          row.nombreVendedor,
          row.vendedorAnterior,
          row.originalFacturaNumber || "",
          row.facturaCreationDate || "",
          row.cancellationReason || "",
          row.siniestro ? "Sí" : "No",
          row.cobranzaJudicial ? "Sí" : "No",
        ];
      }),
    ];

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths
    const columnWidths = [
      { wch: 15 }, // Folio Factura
      { wch: 18 }, // Tipo de Documento
      { wch: 25 }, // ID Nota de Venta
      { wch: 30 }, // Nombre Cliente
      { wch: 15 }, // RUT Cliente
      { wch: 15 }, // Fecha Emisión
      { wch: 20 }, // Condición de Pago
      { wch: 18 }, // Fecha de Expiración
      { wch: 15 }, // Monto Neto
      { wch: 18 }, // Monto Bruto (+ IVA)
      ...(showCostMargin
        ? [{ wch: 15 }, { wch: 15 }, { wch: 10 }] // Costo Total, Margen, % Margen
        : []),
      { wch: 15 }, // Total Pagado
      { wch: 15 }, // Fecha Pago
      { wch: 15 }, // Status Pago
      { wch: 25 }, // Nombre Vendedor
      { wch: 25 }, // Vendedor Anterior
      { wch: 20 }, // Factura Original (NC)
      { wch: 22 }, // Fecha Creación Factura (NC)
      { wch: 25 }, // Motivo Anulación (NC)
      { wch: 12 }, // Siniestro
      { wch: 18 }, // Cobranza Judicial
    ];
    worksheet["!cols"] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Facturas");

    // Generate filename with current date
    const currentDate = format(new Date(), "yyyy-MM-dd");
    const filename = `facturas-${currentDate}.xlsx`;

    // Write and download file
    XLSX.writeFile(workbook, filename);
  } catch (error) {
    console.error("Error exporting facturas to Excel:", error);
    if (error instanceof Error) {
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        name: error.name,
      });
    }
    throw error;
  }
};

interface BIExportData {
  tipoDocumento: string;
  nroFactura: string;
  cliente: string;
  rutCliente: string;
  nombreVendedor: string;
  fechaEmision: string;
  fechaVencimiento: string;
  statusPago: string;
  producto: string;
  descripcionProducto: string;
  subgrupoProducto: string;
  codProd: string;
  cantidad: number;
  unidad: string;
  precioUnitario: number;
  totalNeto: number;
  montoBruto: number;
  // Cost/margin (WAC) — optional; set only for costed factura line rows.
  costoUnitario?: number | null;
  costoTotalLinea?: number | null;
  margenLinea?: number | null;
  margenPctLinea?: number | null;
  condicionPago: string;
  fechaPago: string;
  vendedorAnterior: string;
  fechaCambioVendedor: string;
  facturaOriginalNC: string;
  fechaCreacionFacturaNC: string;
  motivoAnulacionNC: string;
  // Client status columns
  siniestro: boolean;
  cobranzaJudicial: boolean;
}

/**
 * Fetches detailed data for BI export including sales request items and product information
 */
export const fetchFacturasForBIExport = async (
  facturas: SalesRequestDocument[],
  startDate?: Date,
  endDate?: Date,
  userRole?: string,
  currentUserId?: string
): Promise<BIExportData[]> => {
  try {
    // Separate facturas and credit notes
    const facturasOnly = facturas.filter((f) => f.documentType === "factura");
    const creditNotesOnly = facturas.filter(
      (f) => f.documentType === "nota_de_credito"
    );

    // Check for debit notes that might be missing from BI export
    const debitNotesOnly = facturas.filter(
      (f) => f.documentType === "nota_de_debito"
    );

    console.log(
      `📊 BI EXPORT: Processing ${facturasOnly.length} facturas, ${creditNotesOnly.length} credit notes, and ${debitNotesOnly.length} debit notes`
    );

    // Debug: Show debit note details if any exist
    if (debitNotesOnly.length > 0) {
      console.log(
        `🔍 BI EXPORT DEBUG: Found ${debitNotesOnly.length} debit notes in data:`
      );
      debitNotesOnly.forEach((debit) => {
        const bsaleNumber = (debit as any).bsale_number?.toString();
        const amount =
          (debit as any).net_amount || (debit as any).total_amount || 0;
        console.log(`  Debit Note ${bsaleNumber}: $${amount.toLocaleString()}`);
      });

      console.log(
        `🚨 BI EXPORT WARNING: Debit notes are NOT being processed in BI export! This may cause total discrepancies.`
      );
    }

    // Process facturas using existing logic
    const biExportData: BIExportData[] = [];

    // === FACTURAS PROCESSING (existing logic) ===
    if (facturasOnly.length > 0) {
      // Get unique sales request IDs for batch fetching
      const allSalesRequestIds = facturasOnly
        .map((f) => f.salesRequestId)
        .filter(Boolean) as string[];
      const salesRequestIds = Array.from(new Set(allSalesRequestIds));

      // Batch fetch sales requests with seller profile data (existing logic)
      const BATCH_SIZE = 100;
      let salesRequestsWithProfiles: any[] = [];

      for (const batch of batchArray(salesRequestIds, BATCH_SIZE)) {
        const { data, error } = await supabase
          .from("sales_request")
          .select(
            `
            id,
            created_by,
            payment_due_date,
            profiles:created_by(name, lastname, email)
          `
          )
          .in("id", batch);

        if (error) throw error;
        salesRequestsWithProfiles = salesRequestsWithProfiles.concat(
          data || []
        );
      }

      // Batch fetch sales request items with product details
      let salesRequestItems: any[] = [];
      for (const batch of batchArray(salesRequestIds, BATCH_SIZE)) {
        const { data, error } = await supabase
          .from("sales_request_items")
          .select(
            `
            id,
            sales_request_id,
            product_id,
            quantity,
            unit_price,
            discount_percent,
            product_name_snapshot,
            products:product_id (
              short_name,
              long_name,
              description,
              group_description,
              subgroup_description,
              unit_code,
              base_measurement_unit,
              barcode,
              internal_id
            )
          `
          )
          .in("sales_request_id", batch);

        if (error) throw error;
        salesRequestItems = salesRequestItems.concat(data || []);
      }

      // Create lookup maps
      const salesRequestMap = new Map(
        salesRequestsWithProfiles.map((sr) => [sr.id, sr])
      );

      // Group items by sales request ID
      const itemsMap = new Map<string, any[]>();
      salesRequestItems.forEach((item) => {
        const items = itemsMap.get(item.sales_request_id) || [];
        items.push(item);
        itemsMap.set(item.sales_request_id, items);
      });

      // Per-line WAC cost (net CLP) keyed by sales_request_item id.
      const lineCostMap = await facturaCostsService.getLineCostsByItemIds(
        salesRequestItems.map((it) => it.id as string)
      );

      // Enrich facturas data for BI export (no filtering, just add missing fields)
      const regularExportData = await enrichFacturasForExport(
        facturasOnly,
        startDate,
        endDate,
        userRole,
        currentUserId
      );

      // Process each factura (existing logic)
      regularExportData.forEach((factura) => {
        // Get sales request data from our lookup map
        const salesRequestData = salesRequestMap.get(factura.idNotaVenta);
        const sellerProfile = salesRequestData?.profiles as any;
        const fechaVencimiento = factura.fechaExpiracion;

        // Get items for this sales request
        const items = itemsMap.get(factura.idNotaVenta) || [];

        if (items.length > 0) {
          // Calculate total from items for comparison
          const itemsTotal = items.reduce(
            (sum, item) =>
              sum +
              calcLineTotal(
                item.unit_price,
                item.quantity,
                item.discount_percent ?? 0
              ),
            0
          );
          const facturaTotal = factura.montoNeto;
          // Round both values to 2 decimal places to handle floating-point precision errors
          const roundedItemsTotal = Math.round(itemsTotal * 100) / 100;
          const roundedFacturaTotal = Math.round(facturaTotal * 100) / 100;
          const difference = Math.abs(roundedItemsTotal - roundedFacturaTotal);
          const useFacturaTotal = difference > 1.0; // Increased tolerance to $1.00 for minor discrepancies

          if (useFacturaTotal) {
            // Create single row with factura total
            biExportData.push({
              tipoDocumento: factura.tipoDocumento,
              nroFactura: factura.folioFactura,
              cliente: factura.nombreCliente,
              rutCliente: factura.rutCliente,
              nombreVendedor: factura.nombreVendedor,
              fechaEmision: factura.fechaEmision,
              fechaVencimiento,
              statusPago: factura.statusPago,
              producto: "Factura completa (discrepancia detectada)",
              descripcionProducto: "Factura completa con total consolidado",
              subgrupoProducto: "Factura consolidada",
              codProd: "FACTURA_TOTAL",
              cantidad: 1,
              unidad: "Documento",
              precioUnitario: factura.montoNeto,
              totalNeto: factura.montoNeto,
              montoBruto: factura.montoBruto,
              condicionPago: factura.condicionPago,
              fechaPago: factura.fechaPago,
              vendedorAnterior: factura.vendedorAnterior,
              fechaCambioVendedor: factura.fechaCambioVendedor,
              facturaOriginalNC: "",
              fechaCreacionFacturaNC: "",
              motivoAnulacionNC: "",
              siniestro: factura.siniestro,
              cobranzaJudicial: factura.cobranzaJudicial,
            });
          } else {
            // Create one row per product item
            // Calculate total net amount for proportional distribution of gross amount
            const totalNetFromItems = items.reduce(
              (sum, item) =>
                sum +
                calcLineTotal(
                  item.unit_price,
                  item.quantity,
                  item.discount_percent ?? 0
                ),
              0
            );

            items.forEach((item) => {
              const productInfo = item.products as any;
              const lineNetAmount = calcLineTotal(
                item.unit_price,
                item.quantity,
                item.discount_percent ?? 0
              );

              // Calculate proportional gross amount for this line item
              const proportionalMontoBruto =
                totalNetFromItems > 0
                  ? (lineNetAmount / totalNetFromItems) * factura.montoBruto
                  : 0;

              // Per-line WAC cost & margin (net). Cancelled (fully-annulled)
              // facturas carry $0 cost (revenue nets with the NC), matching the
              // KPI cards and v_ventas_app_bi.
              const unitCost = factura.cancelled
                ? undefined
                : lineCostMap.get(item.id);
              const hasCost = unitCost !== undefined && unitCost > 0;
              const costoTotalLinea = hasCost
                ? unitCost! * item.quantity
                : null;
              const margenLinea =
                costoTotalLinea !== null
                  ? lineNetAmount - costoTotalLinea
                  : null;
              const margenPctLinea =
                margenLinea !== null && lineNetAmount !== 0
                  ? (margenLinea / lineNetAmount) * 100
                  : null;

              biExportData.push({
                tipoDocumento: factura.tipoDocumento,
                nroFactura: factura.folioFactura,
                cliente: factura.nombreCliente,
                rutCliente: factura.rutCliente,
                nombreVendedor: factura.nombreVendedor,
                fechaEmision: factura.fechaEmision,
                fechaVencimiento,
                statusPago: factura.statusPago,
                // Prefer the name snapshotted at emission so catalog renames
                // don't retroactively relabel past facturas (matches
                // populate_bi_transactions + OrderItemsTable). Falls back to
                // the live name for pre-snapshot facturas.
                producto:
                  item.product_name_snapshot ||
                  productInfo?.short_name ||
                  "Producto sin nombre",
                descripcionProducto:
                  productInfo?.group_description ||
                  productInfo?.description ||
                  productInfo?.short_name ||
                  "Sin descripción disponible",
                subgrupoProducto:
                  productInfo?.subgroup_description || "Sin subgrupo",
                codProd:
                  productInfo?.barcode || productInfo?.internal_id || "N/A",
                cantidad: item.quantity,
                unidad:
                  productInfo?.unit_code ||
                  productInfo?.base_measurement_unit ||
                  "Unidad",
                precioUnitario: item.unit_price,
                totalNeto: lineNetAmount,
                montoBruto: proportionalMontoBruto,
                costoUnitario: hasCost ? unitCost! : null,
                costoTotalLinea,
                margenLinea,
                margenPctLinea,
                condicionPago: factura.condicionPago,
                fechaPago: factura.fechaPago,
                vendedorAnterior: factura.vendedorAnterior,
                fechaCambioVendedor: factura.fechaCambioVendedor,
                facturaOriginalNC: "",
                fechaCreacionFacturaNC: "",
                motivoAnulacionNC: "",
                siniestro: factura.siniestro,
                cobranzaJudicial: factura.cobranzaJudicial,
              });
            });
          }
        } else {
          // Fallback for facturas without items
          biExportData.push({
            tipoDocumento: factura.tipoDocumento,
            nroFactura: factura.folioFactura,
            cliente: factura.nombreCliente,
            rutCliente: factura.rutCliente,
            nombreVendedor: factura.nombreVendedor,
            fechaEmision: factura.fechaEmision,
            fechaVencimiento,
            statusPago: factura.statusPago,
            producto: "Factura completa",
            descripcionProducto: "Factura sin detalle de productos disponible",
            subgrupoProducto: "Sin detalle de productos",
            codProd: "FACTURA",
            cantidad: 1,
            unidad: "Documento",
            precioUnitario: factura.montoNeto,
            totalNeto: factura.montoNeto,
            montoBruto: factura.montoBruto,
            condicionPago: factura.condicionPago,
            fechaPago: factura.fechaPago,
            vendedorAnterior: factura.vendedorAnterior,
            fechaCambioVendedor: factura.fechaCambioVendedor,
            facturaOriginalNC: "",
            fechaCreacionFacturaNC: "",
            motivoAnulacionNC: "",
            siniestro: factura.siniestro,
            cobranzaJudicial: factura.cobranzaJudicial,
          });
        }
      });
    }

    // === NEW: CREDIT NOTES PROCESSING USING bi_credit_notes TABLE ===
    if (creditNotesOnly.length > 0) {
      console.log(
        `🔍 BI EXPORT: Processing ${creditNotesOnly.length} credit notes using bi_credit_notes table`
      );

      // Filter credit notes for sellers (maintain existing security)
      if (userRole === "seller") {
        console.log(
          "🚫 BI EXPORT: Seller detected - excluding all credit notes from export"
        );
      } else {
        // Extract BSale numbers from credit note documents
        const bsaleNumbers = creditNotesOnly
          .map((doc) => (doc as any).bsale_number?.toString())
          .filter(Boolean);

        if (bsaleNumbers.length > 0) {
          console.log(
            `📋 BI EXPORT: Fetching ${bsaleNumbers.length} credit notes from bi_credit_notes table`
          );

          // First fetch credit note line items from bi_credit_notes table
          const { data: creditNoteItems, error } = await supabase
            .from("bi_credit_notes" as any)
            .select("*")
            .in("bsale_number", bsaleNumbers)
            .order("bsale_number");

          // Fetch cancellation reasons from sales_request_documents (same as regular export)
          const { data: enrichedCreditNoteData } = await supabase
            .from("sales_request_documents")
            .select(
              "bsale_number, motive, administrative_reason, reference, response_data, is_exempt"
            )
            .in("bsale_number", bsaleNumbers)
            .eq("document_type", "nota_de_credito");

          // Create a map for quick lookup of cancellation reasons
          const motivesMap = new Map();
          if (enrichedCreditNoteData) {
            enrichedCreditNoteData.forEach((item: any) => {
              motivesMap.set(item.bsale_number, item);
            });
          }

          // Then enrich with product data from active_products
          let enrichedCreditNoteItems: any[] = creditNoteItems || [];
          if (creditNoteItems && creditNoteItems.length > 0) {
            const productCodes = [
              ...new Set(creditNoteItems.map((item: any) => item.cod_prod)),
            ];

            const { data: products } = await supabase
              .from("active_products" as any)
              .select("sku, group_description, subgroup_description, unit_code")
              .in("sku", productCodes);

            const productsMap = new Map();
            if (products) {
              products.forEach((product: any) => {
                productsMap.set(product.sku, product);
              });
            }

            enrichedCreditNoteItems = creditNoteItems.map((item: any) => {
              const motiveData = motivesMap.get(item.bsale_number);
              return {
                ...item,
                product_group_description:
                  productsMap.get(item.cod_prod)?.group_description || null,
                product_subgroup_description:
                  productsMap.get(item.cod_prod)?.subgroup_description || null,
                product_unit_code:
                  productsMap.get(item.cod_prod)?.unit_code || null,
                // Add cancellation reason from sales_request_documents
                enriched_motive:
                  motiveData?.motive ||
                  motiveData?.administrative_reason ||
                  null,
                // Exempt flag from the NC document (SII 34 -> no IVA)
                is_exempt: motiveData?.is_exempt || false,
              };
            });
          }

          if (error) {
            console.error(
              "Error fetching credit notes from bi_credit_notes:",
              error
            );
          } else {
            console.log(
              `✅ BI EXPORT: Found ${enrichedCreditNoteItems.length} credit note line items`
            );

            // Per-line WAC cost for returns (devoluciones), from the costing
            // module. Price-difference (administrative) NCs have no return cost
            // and stay blank — matching the reviewer's rule.
            const ncCostMap =
              await facturaCostsService.getCreditNoteLineCosts(bsaleNumbers);

            // Convert bi_credit_notes data to BI export format
            enrichedCreditNoteItems.forEach((item: any) => {
              // Format the fecha_emision using dayjs for reliable date handling
              const fechaEmision = item.fecha_emision
                ? dayjs(item.fecha_emision).format("DD/MM/YYYY")
                : "";

              // Cost/margin of the returned product. Reversal convention: cost is
              // negative (mirrors the negative revenue), so the margin reverses
              // the original sale's margin.
              const ncTotalNeto = -(item.total_neto || 0);
              const ncCost = ncCostMap.get(
                `${item.bsale_number}|${item.cod_prod}|${item.bsale_detail_id ?? 0}`
              );
              const ncHasCost = !!ncCost && ncCost.unitCost > 0;
              const ncCostoTotalLinea = ncHasCost
                ? -Math.round(ncCost!.costTotal)
                : null;
              const ncMargenLinea =
                ncCostoTotalLinea != null
                  ? ncTotalNeto - ncCostoTotalLinea
                  : null;
              const ncMargenPctLinea =
                ncMargenLinea != null && ncTotalNeto !== 0
                  ? (ncMargenLinea / ncTotalNeto) * 100
                  : null;

              biExportData.push({
                tipoDocumento: "Nota de Crédito",
                nroFactura: item.bsale_number + "-NC", // Add NC suffix for credit notes
                cliente: item.cliente || "Cliente no encontrado",
                rutCliente: item.rut || "RUT no encontrado",
                nombreVendedor:
                  item.full_name_vendedor || "Vendedor no encontrado",
                fechaEmision,
                fechaVencimiento: "", // Credit notes typically don't have due dates
                statusPago: "Aplicada", // Credit notes are typically applied
                producto: item.producto || "Producto no encontrado",
                descripcionProducto:
                  item.product_group_description ||
                  item.producto ||
                  "Sin descripción",
                subgrupoProducto:
                  item.product_subgroup_description || "Sin subgrupo",
                codProd: item.cod_prod || "SIN-CODIGO",
                cantidad: item.cantidad || 1,
                unidad: item.product_unit_code || "Unidad", // Use product unit code or default
                precioUnitario: -(item.precio_unitario || 0), // Negative for credit notes
                totalNeto: -(item.total_neto || 0), // Negative for credit notes
                montoBruto: grossFromNet(-(item.total_neto || 0), item.is_exempt), // Negative; exempt = no IVA
                costoUnitario: ncHasCost ? ncCost!.unitCost : null,
                costoTotalLinea: ncCostoTotalLinea,
                margenLinea: ncMargenLinea,
                margenPctLinea: ncMargenPctLinea,
                condicionPago: "Nota de Crédito",
                fechaPago: fechaEmision, // Same as emission date for credit notes
                vendedorAnterior: "",
                fechaCambioVendedor: "",
                facturaOriginalNC: item.nro_factura || "",
                fechaCreacionFacturaNC: "",
                motivoAnulacionNC: item.enriched_motive || item.motive || "",
                siniestro: false, // Credit notes from bi_credit_notes don't have client status
                cobranzaJudicial: false,
              });
            });
          }
        }
      }
    }

    // === NEW: DEBIT NOTES PROCESSING ===
    if (debitNotesOnly.length > 0) {
      console.log(
        `🔍 BI EXPORT: Processing ${debitNotesOnly.length} debit notes using same enhanced data as UI`
      );

      // Use the same enriched data that the UI uses for consistency
      const debitNotesExportData = await enrichFacturasForExport(
        debitNotesOnly,
        startDate,
        endDate,
        userRole,
        currentUserId
      );

      console.log(
        `📋 BI EXPORT: Using enriched debit notes data (${debitNotesExportData.length} debit notes) for consistency with UI`
      );

      // Convert enriched debit notes to BI export format
      debitNotesExportData.forEach((debitNote) => {
        // For debit notes, we'll create a single row per document (summary approach)
        biExportData.push({
          tipoDocumento: "Nota de Débito",
          nroFactura: (debitNote as any).folioFactura || "",
          cliente: (debitNote as any).nombreCliente || "",
          rutCliente: (debitNote as any).rutCliente || "",
          nombreVendedor: (debitNote as any).nombreVendedor || "",
          fechaEmision: debitNote.fechaEmision || "",
          fechaVencimiento: (debitNote as any).fechaVencimiento || "",
          statusPago: "Aplicada", // Debit notes are typically applied
          producto: "DEBIT_NOTE_SUMMARY",
          descripcionProducto: "Nota de Débito - Resumen",
          subgrupoProducto: "Nota de Débito",
          codProd: "DEBIT_NOTE",
          unidad: "UN",
          cantidad: 1, // Single summary row
          precioUnitario: debitNote.montoNeto || 0, // Positive for debit notes
          totalNeto: debitNote.montoNeto || 0, // Positive for debit notes
          montoBruto: debitNote.montoBruto || 0, // Positive for debit notes
          condicionPago: debitNote.condicionPago || "Nota de Débito",
          fechaPago: debitNote.fechaPago || "",
          vendedorAnterior: "",
          fechaCambioVendedor: "",
          facturaOriginalNC: "",
          fechaCreacionFacturaNC: "",
          motivoAnulacionNC: "",
          siniestro: debitNote.siniestro,
          cobranzaJudicial: debitNote.cobranzaJudicial,
        });
      });
    }

    const totalFacturasRows = biExportData.filter(
      (row) =>
        row.tipoDocumento !== "Nota de Crédito" &&
        row.tipoDocumento !== "Nota de Débito"
    ).length;
    const totalCreditNotesRows = biExportData.filter(
      (row) => row.tipoDocumento === "Nota de Crédito"
    ).length;
    const totalDebitNotesRows = biExportData.filter(
      (row) => row.tipoDocumento === "Nota de Débito"
    ).length;

    console.log(
      `✅ BI EXPORT COMPLETE: ${totalFacturasRows} factura rows + ${totalCreditNotesRows} credit note rows + ${totalDebitNotesRows} debit note rows = ${biExportData.length} total rows`
    );

    return biExportData;
  } catch (error) {
    console.error("Error in enhanced fetchFacturasForBIExport:", error);
    throw error;
  }
};

/**
 * Exports facturas data to Excel file for BI purposes
 */
export const exportFacturasForBI = async (
  facturas: SalesRequestDocument[],
  startDate?: Date,
  endDate?: Date,
  userRole?: string,
  currentUserId?: string
): Promise<void> => {
  try {
    // Detect if we're filtering for June 2025 (any date range within June 2025)
    const isFilteringJune2025 =
      startDate &&
      endDate &&
      startDate.getFullYear() === 2025 &&
      endDate.getFullYear() === 2025 &&
      startDate.getMonth() === 5 &&
      endDate.getMonth() === 5;

    // Detect if we're filtering any range that includes May 2025
    const isFilteringRangeIncludingMay2025 =
      startDate &&
      endDate &&
      startDate.getFullYear() === 2025 &&
      endDate.getFullYear() === 2025 &&
      startDate.getMonth() <= 4 && // Starts in May or earlier
      endDate.getMonth() >= 4; // Ends in May or later

    // Detect if we're filtering for July 2025 or later (when all documents should be included)
    const isFilteringJuly2025OrLater =
      startDate &&
      endDate &&
      ((startDate.getFullYear() === 2025 && startDate.getMonth() >= 6) ||
        startDate.getFullYear() > 2025);

    // Apply date-based filtering logic for cancelled documents
    let facturasForExport = isFilteringJuly2025OrLater
      ? facturas // Include ALL facturas for July 2025 and later
      : facturas.filter((factura) => {
          const isCancelled = (factura as any).cancelled === true;

          // If not cancelled, always include
          if (!isCancelled) {
            return true;
          }

          // Special case for ranges including May 2025: Include cancelled facturas to match their credit notes
          // This ensures proper accounting when credit notes reference cancelled facturas
          if (isFilteringRangeIncludingMay2025) {
            console.log(
              `Range including May 2025: Including cancelled factura ${(factura as any).bsale_number} to match credit notes`
            );
            return true;
          }

          // Special case for June 2025 only ranges: Include all cancelled facturas to match their credit notes
          // This ensures proper accounting when credit notes reference cancelled facturas
          if (isFilteringJune2025) {
            console.log(
              `June 2025: Including cancelled factura ${(factura as any).bsale_number} to match credit notes`
            );
            return true;
          }

          // If cancelled, check if we should include based on timing (original logic for pre-July 2025)
          const createdAt = factura.createdAt
            ? new Date(factura.createdAt)
            : null;
          const cancellationDate = (factura as any).cancellationDate
            ? new Date((factura as any).cancellationDate)
            : null;

          // If we don't have proper dates, exclude cancelled facturas (safe default)
          if (!createdAt || !cancellationDate) {
            return false;
          }

          // Check if creation and cancellation are in the same month/year
          const creationMonth = createdAt.getMonth();
          const creationYear = createdAt.getFullYear();
          const cancellationMonth = cancellationDate.getMonth();
          const cancellationYear = cancellationDate.getFullYear();

          const cancelledInSameMonth =
            creationMonth === cancellationMonth &&
            creationYear === cancellationYear;

          // Include cancelled facturas ONLY if they were cancelled in a different month
          return !cancelledInSameMonth;
        });

    // Filter out soft-deleted documents
    const beforeDeletedFilterBI = facturasForExport.length;
    facturasForExport = facturasForExport.filter((factura) => {
      if (factura.deleted_at) {
        const bsaleNumber = (factura as any).bsale_number?.toString();
        console.log(
          `🗑️ BI EXPORT FILTER: Excluding deleted document ${bsaleNumber} (deleted at: ${factura.deleted_at})`
        );
        return false;
      }
      return true;
    });
    console.log(
      `🗑️ BI DELETED FILTER: Removed ${beforeDeletedFilterBI - facturasForExport.length} soft-deleted documents`
    );

    // Apply seller filtering to the BI export data as well
    if (userRole === "seller" && currentUserId) {
      console.log(
        `🔒 BI EXPORT: Applying additional seller filtering to ${facturasForExport.length} documents`
      );
      facturasForExport = facturasForExport.filter((factura) => {
        const bsaleNumber = (factura as any).bsale_number?.toString();

        // Exclude BSale Directo documents
        if (factura.isTemporary === true) {
          console.log(
            `🚫 BI EXPORT: Excluding BSale Directo document ${bsaleNumber}`
          );
          return false;
        }

        // Exclude specific problematic credit notes for sellers (only July 2025 specific ones)
        if (["10636", "10637"].includes(bsaleNumber)) {
          console.log(
            `🚫 BI EXPORT: Excluding problematic credit note ${bsaleNumber} for seller`
          );
          return false;
        }

        // Only include documents with sales requests (no standalone credit notes)
        if (!factura.salesRequestId) {
          console.log(
            `🚫 BI EXPORT: Excluding document without sales request ${bsaleNumber}`
          );
          return false;
        }

        return true;
      });
      console.log(
        `🔒 BI EXPORT: After seller filtering: ${facturasForExport.length} documents remaining`
      );
    }

    // Add special facturas for ranges including May 2025 if they're not already included
    if (isFilteringRangeIncludingMay2025) {
      const specialBsaleNumbers = ["232104", "231827"];
      const includedBsaleNumbers = new Set(
        facturasForExport
          .map((f) => (f as any).bsale_number?.toString())
          .filter(Boolean)
      );

      let missingSpecialFacturas = facturas.filter((factura) => {
        const bsaleNumber = (factura as any).bsale_number?.toString();
        return (
          specialBsaleNumbers.includes(bsaleNumber) &&
          !includedBsaleNumbers.has(bsaleNumber)
        );
      });

      // Apply seller filtering to special facturas as well
      if (userRole === "seller" && currentUserId) {
        missingSpecialFacturas = missingSpecialFacturas.filter((factura) => {
          // Only include if it has a sales request from this seller
          return factura.salesRequestId && !factura.isTemporary;
        });
        console.log(
          `🔒 BI EXPORT: Special facturas after seller filtering: ${missingSpecialFacturas.length}`
        );
      }

      if (missingSpecialFacturas.length > 0) {
        facturasForExport.push(...missingSpecialFacturas);
      }
    }

    // Fetch detailed data
    const exportData = await fetchFacturasForBIExport(
      facturasForExport,
      startDate,
      endDate,
      userRole,
      currentUserId
    );

    // Sort exportData: Nota de débito first, then Facturas, then Notas de crédito, then by bsale number
    const sortedExportData =
      exportData?.sort((a, b) => {
        const getDocumentTypePriority = (tipoDocumento: string): number => {
          if (tipoDocumento.toLowerCase().includes("débito")) return 1;
          if (tipoDocumento.toLowerCase().includes("factura")) return 2;
          if (tipoDocumento.toLowerCase().includes("crédito")) return 3;
          return 4; // fallback
        };

        const priorityDiff =
          getDocumentTypePriority(a.tipoDocumento) -
          getDocumentTypePriority(b.tipoDocumento);

        // If same document type, sort by bsale number (numeric sort)
        if (priorityDiff === 0) {
          const extractBsaleNumber = (nroFactura: string): number => {
            // Remove suffixes like "-NC" and "(ANULADO)" and extract numeric part
            const cleanFolio = nroFactura
              .replace(/-NC.*$/, "")
              .replace(/\s*\(ANULADO\).*$/, "");
            const number = parseInt(cleanFolio, 10);
            return isNaN(number) ? 0 : number;
          };

          return (
            extractBsaleNumber(b.nroFactura) - extractBsaleNumber(a.nroFactura)
          );
        }

        return priorityDiff;
      }) || [];

    console.log(
      `📊 BI EXCEL EXPORT: Sorted ${sortedExportData.length} documents by type and bsale number (descending)`
    );

    // June 2025: No hard-coded corrections - use actual calculated values
    const isJune2025 =
      startDate &&
      endDate &&
      startDate.getFullYear() === 2025 &&
      endDate.getFullYear() === 2025 &&
      startDate.getMonth() === 5 &&
      endDate.getMonth() === 5;

    if (isJune2025) {
      const currentTotal = sortedExportData.reduce(
        (sum, row) => sum + row.totalNeto,
        0
      );
      console.log(
        `🔍 JUNE 2025 BI EXPORT: Using actual calculated total: ${currentTotal.toLocaleString()}`
      );
    }

    // Cost & margin are sensitive — sellers never get these columns.
    const showCostMargin = userRole !== "seller";

    // Create worksheet data with headers (no summary for BI export)
    const worksheetData = [
      [
        "Tipo de Documento",
        "Nro Factura",
        "Fecha Emisión",
        "RUT Cliente",
        "Cliente",
        "Nombre Vendedor",
        "Cod. Prod",
        "Producto",
        "Descripción de Grupo",
        "Subgrupo",
        "Cantidad",
        "Unidad",
        "Precio Unitario",
        "Total Neto",
        ...(showCostMargin
          ? ["Costo Unitario", "Costo Total", "Margen", "% Margen"]
          : []),
        "Monto Bruto",
        "Condición",
        "Fecha Vencimiento",
        "Status Pago",
        "Fecha Pago",
        "Vendedor Anterior",
        "Fecha Cambio Vendedor",
        "Factura Original (NC)",
        "Fecha Creación Factura (NC)",
        "Motivo Anulación (NC)",
        "Siniestro",
        "Cobranza Judicial",
      ],
      ...sortedExportData.map((row) => [
        row.tipoDocumento,
        row.nroFactura,
        row.fechaEmision,
        row.rutCliente,
        row.cliente,
        row.nombreVendedor,
        row.codProd,
        row.producto,
        row.descripcionProducto,
        row.subgrupoProducto,
        row.cantidad,
        row.unidad,
        row.precioUnitario,
        row.totalNeto,
        ...(showCostMargin
          ? [
              row.costoUnitario != null ? Math.round(row.costoUnitario) : "",
              row.costoTotalLinea != null
                ? Math.round(row.costoTotalLinea)
                : "",
              row.margenLinea != null ? Math.round(row.margenLinea) : "",
              row.margenPctLinea != null
                ? Number(row.margenPctLinea.toFixed(1))
                : "",
            ]
          : []),
        row.montoBruto,
        row.condicionPago,
        row.fechaVencimiento,
        row.statusPago,
        row.fechaPago,
        row.vendedorAnterior,
        row.fechaCambioVendedor,
        row.facturaOriginalNC,
        row.fechaCreacionFacturaNC,
        row.motivoAnulacionNC,
        row.siniestro ? "Sí" : "No",
        row.cobranzaJudicial ? "Sí" : "No",
      ]),
    ];

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths
    const columnWidths = [
      { wch: 15 }, // Tipo de Documento
      { wch: 15 }, // Nro Factura
      { wch: 15 }, // Fecha Emisión
      { wch: 15 }, // RUT Cliente
      { wch: 30 }, // Cliente
      { wch: 25 }, // Nombre Vendedor
      { wch: 15 }, // Cod. Prod
      { wch: 40 }, // Producto
      { wch: 45 }, // Descripción de Grupo
      { wch: 35 }, // Subgrupo
      { wch: 12 }, // Cantidad
      { wch: 15 }, // Unidad
      { wch: 18 }, // Precio Unitario
      { wch: 15 }, // Total Neto
      ...(showCostMargin
        ? [{ wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 10 }] // Costo Unitario, Costo Total, Margen, % Margen
        : []),
      { wch: 15 }, // Monto Bruto
      { wch: 15 }, // Condición
      { wch: 18 }, // Fecha Vencimiento
      { wch: 15 }, // Status Pago
      { wch: 15 }, // Fecha Pago
      { wch: 20 }, // Vendedor Anterior
      { wch: 20 }, // Fecha Cambio Vendedor
      { wch: 20 }, // Factura Original (NC)
      { wch: 22 }, // Fecha Creación Factura (NC)
      { wch: 25 }, // Motivo Anulación (NC)
      { wch: 12 }, // Siniestro
      { wch: 18 }, // Cobranza Judicial
    ];
    worksheet["!cols"] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Facturas BI");

    // Generate filename with current date
    const currentDate = format(new Date(), "yyyy-MM-dd");
    const filename = `facturas-bi-${currentDate}.xlsx`;

    // Write and download file
    XLSX.writeFile(workbook, filename);
  } catch (error) {
    console.error("Error exporting facturas for BI:", error);
    throw error;
  }
};

/**
 * Interface for sales notes export data
 */
interface SalesNotesExportData {
  vendedor: string;
  factura: string;
  status: string;
  cliente: string;
  direccion: string;
  monto_neto: number;
  monto_con_iva: number;
  condicion_de_venta: string;
  fecha_emision: string;
  fecha_vencimiento: string;
  dias_vencidos: number;
  estado_vencimiento: string;
  estado_pago: string;
}

/**
 * Exports sales notes data to Excel file
 */
export const exportSalesNotesToExcel = async (
  salesNotes: any[],
  clientNames: Record<string, string>,
  getSellerInfo: (id: string) => { name: string; lastname: string },
  getConditionById: (
    id: string
  ) => { name: string; maximumDays?: number } | null,
  clientSalesConditions: Record<string, string>,
  salesRequestItems: Record<string, any[]>,
  options?: {
    dateRange?: { from?: Date; to?: Date };
    userRole?: string;
    currentUserId?: string;
    viewAllSellers?: boolean;
    fetchAllDataForDateRange?: boolean;
  }
): Promise<void> => {
  try {
    const today = new Date();

    // If fetchAllDataForDateRange is enabled and we have date range, fetch all data
    let dataToExport = salesNotes;
    if (options?.fetchAllDataForDateRange && options?.dateRange) {
      console.log("Fetching all sales requests for date range export...");
      const allSalesRequests = await fetchAllSalesRequestsForExport(
        options.dateRange.from,
        options.dateRange.to,
        options.userRole,
        options.currentUserId,
        options.viewAllSellers
      );
      dataToExport = allSalesRequests;
      console.log(
        `Fetched ${dataToExport.length} sales requests for export (was ${salesNotes.length})`
      );
    }

    // Get unique client IDs to fetch addresses
    const clientIds = [...new Set(dataToExport.map((order) => order.clientId))];

    // Get unique sales request IDs to fetch invoice numbers
    const salesRequestIds = dataToExport.map((order) => order.id);

    // Fetch client addresses
    const { data: addresses, error: addressError } = await supabase
      .from("clients")
      .select(
        `
        id,
        addresses!clients_fiscal_address_id_fkey(
          street,
          number,
          city,
          comuna,
          complement
        )
      `
      )
      .in("id", clientIds);

    if (addressError) {
      console.error("Error fetching client addresses:", addressError);
    }

    // Fetch invoice numbers for accepted/dispatched orders
    const acceptedOrDispatchedStatuses = [
      "accepted",
      "delivered",
      "in_transit",
    ];
    const eligibleOrderIds = dataToExport
      .filter((order) => acceptedOrDispatchedStatuses.includes(order.status))
      .map((order) => order.id);

    let invoiceDocuments: any[] = [];
    if (eligibleOrderIds.length > 0) {
      const { data: documents, error: documentsError } = await supabase
        .from("sales_request_documents")
        .select("sales_request_id, bsale_number, filename")
        .in("sales_request_id", eligibleOrderIds)
        .eq("document_type", "factura")
        .is("deleted_at", null);

      if (documentsError) {
        console.error("Error fetching invoice documents:", documentsError);
      } else {
        invoiceDocuments = documents || [];
      }
    }

    // Create lookup maps
    const addressMap = new Map(
      addresses?.map((client: any) => [
        client.id,
        client.addresses
          ? {
              street: client.addresses.street || "",
              number: client.addresses.number || "",
              city: client.addresses.city || "",
              comuna: client.addresses.comuna || "",
              complement: client.addresses.complement || "",
            }
          : null,
      ]) || []
    );

    const invoiceMap = new Map(
      invoiceDocuments.map((doc: any) => [
        doc.sales_request_id,
        doc.bsale_number || extractInvoiceNumberFromFilename(doc.filename),
      ])
    );

    // Convert sales notes to export format
    const exportData: SalesNotesExportData[] = dataToExport.map((order) => {
      const sellerInfo = getSellerInfo(order.createdBy);

      // Get sales condition and payment terms
      let condition = null;
      let paymentTerms = 0;

      if (order.salesConditionId) {
        condition = getConditionById(order.salesConditionId);
      } else if (clientSalesConditions[order.clientId]) {
        condition = getConditionById(clientSalesConditions[order.clientId]);
      }

      if (condition) {
        paymentTerms = condition.maximumDays || 0;
      }

      // Format customer address
      const address = addressMap.get(order.clientId);
      const customerAddress = address
        ? `${address.street} ${address.number}, ${address.city}${address.comuna ? `, ${address.comuna}` : ""}${address.complement ? ` (${address.complement})` : ""}`
        : "Dirección no disponible";

      // Calculate correct due date based on sales condition
      let dueDate = new Date();

      // Special case: transferencia anticipada means immediate payment (due date = creation date)
      const isAdvanceTransfer = condition?.name
        ?.toLowerCase()
        .includes("transferencia anticipada");

      if (isAdvanceTransfer) {
        dueDate = new Date(order.createdAt);
      } else if (paymentTerms > 0) {
        const creationDate = new Date(order.createdAt);
        dueDate = new Date(creationDate);
        dueDate.setDate(dueDate.getDate() + paymentTerms);
      } else {
        dueDate = new Date(order.createdAt);
      }

      // Calculate days past expiration
      const daysPastExpiration = Math.ceil(
        (today.getTime() - dueDate.getTime()) / (1000 * 3600 * 24)
      );
      const isExpired = daysPastExpiration > 0;
      const isAboutToExpire =
        daysPastExpiration <= 0 && daysPastExpiration > -5;

      // Determine expiration status
      let expirationStatus = "";
      if (isExpired) {
        expirationStatus = "Vencida";
      } else if (isAboutToExpire) {
        expirationStatus = "Pronto a vencer";
      } else {
        expirationStatus = "Vigente";
      }

      // Get invoice number for accepted/dispatched orders
      const invoiceNumber = acceptedOrDispatchedStatuses.includes(order.status)
        ? invoiceMap.get(order.id) || `NV-${order.id.substring(0, 8)}`
        : `NV-${order.id.substring(0, 8)}`;

      // Determine status - change "test" to "rejected"
      let status = order.status;
      if (status === "test") {
        status = "rejected";
      }

      // Translate status to Spanish
      const statusTranslations = {
        delivered: "Entregado",
        accepted: "Aceptado",
        in_transit: "En Tránsito",
        pending: "Pendiente",
        pending_pricing: "Cobranza",
        pending_credit: "Crédito Insuficiente",
        prepaid: "Esperando Pago",
        rejected: "Rechazado",
      };
      const translatedStatus = statusTranslations[status] || status;

      // Determine payment status based on paid amount
      let paymentStatus = "";
      const paidAmount = order.paidAmount || 0;
      const totalAmount = order.totalAmount || 0;

      if (paidAmount >= totalAmount) {
        paymentStatus = "Pagado";
      } else if (paidAmount > 0) {
        paymentStatus = "Pago Parcial";
      } else {
        paymentStatus = "Pendiente";
      }

      return {
        vendedor: `${sellerInfo.name} ${sellerInfo.lastname}`,
        factura: invoiceNumber,
        status: translatedStatus,
        cliente: clientNames[order.clientId] || "Cliente sin nombre",
        direccion: customerAddress,
        monto_neto: order.totalAmount,
        monto_con_iva: grossFromNet(
          order.totalAmount,
          (order as any).isExento ?? (order as any).is_exento
        ),
        condicion_de_venta: condition?.name || "No especificada",
        fecha_emision: new Date(order.createdAt).toLocaleDateString("es-CL"),
        fecha_vencimiento: dueDate.toLocaleDateString("es-CL"),
        dias_vencidos: daysPastExpiration > 0 ? daysPastExpiration : 0,
        estado_vencimiento: expirationStatus,
        estado_pago: paymentStatus,
      };
    });

    // Create worksheet data with Spanish headers in the requested order
    const worksheetData = [
      [
        "Vendedor",
        "Número de Factura",
        "Estado",
        "Cliente",
        "Dirección",
        "Monto Neto",
        "Monto con IVA",
        "Condición de Venta",
        "Fecha de Emisión",
        "Fecha de Vencimiento",
        "Días Vencidos",
        "Estado de Vencimiento",
        "Estado de Pago",
      ],
      ...exportData.map((row) => [
        row.vendedor,
        row.factura,
        row.status,
        row.cliente,
        row.direccion,
        row.monto_neto,
        row.monto_con_iva,
        row.condicion_de_venta,
        row.fecha_emision,
        row.fecha_vencimiento,
        row.dias_vencidos,
        row.estado_vencimiento,
        row.estado_pago,
      ]),
    ];

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths
    const columnWidths = [
      { wch: 25 }, // Vendedor
      { wch: 20 }, // Número de Factura
      { wch: 15 }, // Estado
      { wch: 30 }, // Cliente
      { wch: 40 }, // Dirección
      { wch: 15 }, // Monto Neto
      { wch: 15 }, // Monto con IVA
      { wch: 20 }, // Condición de Venta
      { wch: 15 }, // Fecha de Emisión
      { wch: 18 }, // Fecha de Vencimiento
      { wch: 15 }, // Días Vencidos
      { wch: 20 }, // Estado de Vencimiento
      { wch: 15 }, // Estado de Pago
    ];
    worksheet["!cols"] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Notas de Venta");

    // Generate filename with current date
    const currentDate = format(new Date(), "yyyy-MM-dd");
    const filename = `notas-venta-${currentDate}.xlsx`;

    // Write and download file
    XLSX.writeFile(workbook, filename);
  } catch (error) {
    console.error("Error exporting sales notes to Excel:", error);
    throw error;
  }
};

/**
 * Helper function to extract invoice number from filename
 */
const extractInvoiceNumberFromFilename = (filename: string | null): string => {
  if (!filename) return "N/A";
  const match = filename.match(/factura-(\d+)\.pdf/);
  return match ? match[1] : "N/A";
};

/**
 * Fetches all sales requests within a date range for export purposes
 * Bypasses pagination to get complete dataset
 */
export const fetchAllSalesRequestsForExport = async (
  startDate?: Date,
  endDate?: Date,
  userRole?: string,
  currentUserId?: string,
  viewAllSellers: boolean = false
): Promise<any[]> => {
  try {
    console.log("Fetching all sales requests for export with date range:", {
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      userRole,
      viewAllSellers,
    });

    // Build the base query with role-based filtering
    let query = supabase
      .from("sales_request")
      .select(
        `
        *,
        clients:client_id(id, name, risk_checked, is_test),
        profiles:created_by(id, name, lastname, email, role)
      `
      )
      .is("deleted_at", null); // Exclude soft-deleted records

    // Apply date range filter if provided
    if (startDate) {
      query = query.gte("created_at", startDate.toISOString());
    }
    if (endDate) {
      // Add one day to endDate to include the entire end date
      const endDatePlusOne = new Date(endDate);
      endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);
      query = query.lt("created_at", endDatePlusOne.toISOString());
    }

    // Apply role-based filtering
    if (userRole === "seller" && currentUserId && !viewAllSellers) {
      query = query.eq("created_by", currentUserId);
    } else if (userRole === "cobranza" && !viewAllSellers) {
      // Payments role only sees prepaid, payment_to_check, and accepted paid sales requests
      query = query.or(
        "status.eq.prepaid,status.eq.payment_to_check,and(status.eq.accepted,paid.eq.true)"
      );
    } else if (userRole === "transportista") {
      // Transportista role only sees orders assigned to them and only "in_transit" and "delivered" statuses
      query = query
        .eq("assigned_transportista", currentUserId)
        .or("status.eq.in_transit,status.eq.delivered");
    }
    // All other roles (including superadmin) see all sales requests by default

    // Always exclude test clients from exports
    query = query.eq("clients.is_test", false);

    // Execute the query with appropriate ordering
    const { data: salesRequestsData, error: salesRequestsError } =
      await query.order("created_at", { ascending: false });

    if (salesRequestsError) throw salesRequestsError;

    if (salesRequestsData) {
      console.log(
        `Fetched ${salesRequestsData.length} sales requests for export`
      );

      // Convert to our frontend interface format
      const formattedRequests = salesRequestsData.map((req) => ({
        id: req.id,
        clientId: req.client_id,
        createdBy: req.created_by,
        approvedRejectedBy: req.approved_rejected_by || undefined,
        status: req.status as
          | "pending"
          | "pending_pricing"
          | "pending_credit"
          | "prepaid"
          | "accepted"
          | "rejected"
          | "in_transit"
          | "delivered"
          | "payment_to_check"
          | "test",
        paid: req.paid,
        paymentTerms: req.payment_terms,
        paymentType: req.payment_type as "future" | "prepaid",
        paymentDueDate: req.payment_due_date,
        totalAmount: req.total_amount,
        priceWithIva: req.price_with_iva,
        paidAmount: req.paid_amount,
        hasPricingWarning: (req as any).has_pricing_warning || false,
        comments: req.comments || undefined,
        internal_notes: req.internal_notes || undefined,
        factura_observations: req.factura_observations || undefined,
        deliveryDate: req.delivery_date || undefined,
        deliveryAddressId: req.delivery_address_id || undefined,
        // Map the address fields
        deliveryStreet: req.delivery_street || undefined,
        deliveryNumber: req.delivery_number || undefined,
        deliveryComplement: req.delivery_complement || undefined,
        deliveryCity: req.delivery_city || undefined,
        deliveryState: req.delivery_state || undefined,
        deliveryComuna: req.delivery_comuna || undefined,
        rejectedReason: req.rejected_reason || undefined,
        createdAt: req.created_at,
        updatedAt: req.updated_at,
        salesConditionId: req.sales_condition_id || undefined,
      }));

      return formattedRequests;
    }

    return [];
  } catch (error) {
    console.error("Error fetching sales requests for export:", error);
    throw error;
  }
};

// Utility function to fetch client info from BSale API
const fetchBSaleClientInfo = async (clientId: string) => {
  try {
    const response = await bsaleFetch(`/clients/${clientId}.json`);

    if (!response.ok) {
      console.error(
        `Failed to fetch BSale client ${clientId}: ${response.status}`
      );
      return null;
    }

    const clientData = await response.json();
    return {
      id: clientData.id,
      name: clientData.company || clientData.firstName || "Cliente BSale",
      tax_id: clientData.code || "N/A",
      sales_conditions: null,
    };
  } catch (error) {
    console.error(`Error fetching BSale client ${clientId}:`, error);
    return null;
  }
};

// Utility function to extract client ID from BSale document response
const extractClientIdFromBSaleDocument = (
  bsaleNumber: string,
  allFacturas: any[]
) => {
  // Find the factura with the matching bsale_number
  const factura = allFacturas.find(
    (f) => String((f as any).bsale_number) === String(bsaleNumber)
  );

  if (!factura || !(factura as any).response_data) {
    return null;
  }

  const responseData = (factura as any).response_data;

  // Try to extract client ID from different possible locations in the response

  // Method 1: Direct client object
  if (responseData.client?.id) {
    return responseData.client.id;
  }

  if (responseData.client?.href) {
    // Extract client ID from href like "https://api.bsale.io/v1/clients/123.json"
    const match = responseData.client.href.match(/\/clients\/(\d+)\.json/);
    if (match) {
      return match[1];
    }
  }

  // Method 2: For credit notes, check credit_note.client
  if (responseData.credit_note?.client?.id) {
    return responseData.credit_note.client.id;
  }

  if (responseData.credit_note?.client?.href) {
    const match = responseData.credit_note.client.href.match(
      /\/clients\/(\d+)\.json/
    );
    if (match) {
      return match[1];
    }
  }

  // Method 2.5: For credit notes with reference_document (BSale returns/credit notes)
  if (responseData.reference_document?.id) {
    // This is a credit note that references an original document
    // We need to fetch the original document to get client info
    // For now, we'll return a special marker to indicate we need to fetch the reference
    return `REF_DOC:${responseData.reference_document.id}`;
  }

  // Method 3: Look for any nested client references in various BSale response structures
  const findClientId = (obj: any, visited = new Set()): string | null => {
    if (!obj || typeof obj !== "object" || visited.has(obj)) {
      return null;
    }

    visited.add(obj);

    // If this object has a client with an id, return it
    if (obj.client?.id) {
      return obj.client.id;
    }

    // If this object has a client href, extract the ID
    if (obj.client?.href) {
      const match = obj.client.href.match(/\/clients\/(\d+)\.json/);
      if (match) {
        return match[1];
      }
    }

    // Recursively search in nested objects
    for (const key in obj) {
      if (
        Object.prototype.hasOwnProperty.call(obj, key) &&
        typeof obj[key] === "object"
      ) {
        const result = findClientId(obj[key], visited);
        if (result) {
          return result;
        }
      }
    }

    return null;
  };

  // Try the recursive search as a last resort
  const foundClientId = findClientId(responseData);
  if (foundClientId) {
    return foundClientId;
  }

  return null;
};

/**
 * Fetch detailed payment data with all related information
 */
const fetchDetailedPaymentData = async (payments: any[]) => {
  // Get payment IDs to fetch additional data
  const salesRequestIds = payments
    .map((p) => {
      // Try multiple ways to get the sales request ID
      if (p.sales_request?.id) return p.sales_request.id;
      if (typeof p.sales_request === "string") return p.sales_request;
      if (p.sales_request_id) return p.sales_request_id;
      return null;
    })
    .filter(Boolean);

  console.log("Payments being processed:", payments.length);
  console.log("Sales request IDs extracted:", salesRequestIds.length);
  const clientIds = payments
    .map((p) => p.client?.id || p.client)
    .filter(Boolean);

  // Fetch payment proofs
  let paymentProofs: any[] = [];
  if (salesRequestIds.length > 0) {
    const { data: proofsData } = await supabase
      .from("payment_proofs")
      .select("*")
      .in("sales_request_id", salesRequestIds);
    paymentProofs = proofsData || [];
  }

  // Fetch seller/creator information
  let creators: any[] = [];
  if (salesRequestIds.length > 0) {
    const { data: salesRequestData } = await supabase
      .from("sales_request")
      .select(
        `
        id,
        created_by,
        created_at,
        status,
        payment_terms,
        payment_type,
        delivery_date,
        comments,
        profiles:created_by(name, lastname, email, role)
      `
      )
      .in("id", salesRequestIds);
    creators = salesRequestData || [];
  }

  // Fetch sales request items
  let salesRequestItems: any[] = [];
  if (salesRequestIds.length > 0) {
    const { data: itemsData } = await supabase
      .from("sales_request_items")
      .select(
        `
        *,
        products(short_name, long_name, description)
      `
      )
      .in("sales_request_id", salesRequestIds);
    salesRequestItems = itemsData || [];
  }

  // Fetch invoice documents (facturas) for sales requests
  let invoiceDocuments: any[] = [];
  if (salesRequestIds.length > 0) {
    console.log(
      `Fetching invoice documents for ${salesRequestIds.length} sales requests...`
    );

    // Batch the queries to avoid timeout issues
    const BATCH_SIZE = 20;
    const batches = [];
    for (let i = 0; i < salesRequestIds.length; i += BATCH_SIZE) {
      batches.push(salesRequestIds.slice(i, i + BATCH_SIZE));
    }

    console.log(
      `Processing ${batches.length} batches of max ${BATCH_SIZE} IDs each`
    );

    const allDocuments: any[] = [];

    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];
      console.log(
        `Processing batch ${i + 1}/${batches.length} with ${batch.length} IDs`
      );

      try {
        const { data: batchData, error: batchError } = await supabase
          .from("sales_request_documents")
          .select(
            "sales_request_id, bsale_number, filename, document_type, cancelled, deleted_at"
          )
          .in("sales_request_id", batch)
          .eq("document_type", "factura")
          .eq("cancelled", false)
          .is("deleted_at", null);

        if (batchError) {
          console.error(`Error in batch ${i + 1}:`, batchError);
        } else {
          console.log(
            `Batch ${i + 1} returned ${batchData?.length || 0} documents`
          );
          if (batchData) {
            allDocuments.push(...batchData);
          }
        }

        // Small delay to prevent overwhelming the database
        if (i < batches.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      } catch (error) {
        console.error(`Network error in batch ${i + 1}:`, error);
      }
    }

    invoiceDocuments = allDocuments;
    console.log(`Total invoice documents found: ${invoiceDocuments.length}`);

    // Check if our problem document was found
    const problemDoc = invoiceDocuments.find(
      (doc) => doc.sales_request_id === "660cb770-476a-4ca5-9f6d-f0188660ea2e"
    );
    console.log("Problem document found:", problemDoc);
  }

  return {
    paymentProofs,
    creators,
    salesRequestItems,
    invoiceDocuments,
  };
};

/**
 * Export payments data to Excel format
 */
export const exportPaymentsToExcel = async (
  payments: any[],
  latePayments: any[],
  outstandingPayments: any[],
  stats: any,
  dateRange?: { from?: Date; to?: Date }
): Promise<void> => {
  try {
    // Use database function for better performance
    console.log("Fetching payments using database function...");

    // Prepare date parameters
    let dateFrom = null;
    let dateTo = null;
    let recordLimit = 5000;

    if (dateRange?.from) {
      const fromDate = new Date(dateRange.from);
      fromDate.setHours(0, 0, 0, 0);
      dateFrom = fromDate.toISOString().split("T")[0]; // YYYY-MM-DD format

      if (dateRange.to) {
        // Fix timezone issue: extract date before timezone conversion
        const year = dateRange.to.getFullYear();
        const month = String(dateRange.to.getMonth() + 1).padStart(2, "0");
        const day = String(dateRange.to.getDate()).padStart(2, "0");
        dateTo = `${year}-${month}-${day}`; // YYYY-MM-DD format without timezone conversion
      }

      // When date filtering, allow more records
      recordLimit = 10000;
    }

    const { data: paymentData, error } = await supabase.rpc(
      "get_payments_for_excel_export",
      {
        date_from: dateFrom,
        date_to: dateTo,
        record_limit: recordLimit,
      }
    );

    if (error) {
      console.error("Error fetching payments with database function:", error);
      throw new Error("Failed to fetch payments data");
    }

    console.log(
      "Database function returned:",
      (paymentData as any)?.length || 0,
      "payments"
    );
    console.log(
      "Sample payment IDs:",
      (paymentData as any)?.slice(0, 5).map((p: any) => p.payment_id)
    );
    console.log(
      "Sample verified_at values:",
      (paymentData as any)?.slice(0, 5).map((p: any) => ({
        id: p.payment_id,
        verified_at: p.verified_at,
      }))
    );

    // Transform database function result to match expected format
    const allPayments =
      (paymentData as any)?.map((row: any) => ({
        id: row.payment_id,
        created_at: row.payment_created_at,
        verified_at: row.verified_at,
        amount: row.payment_amount,
        payment_method: row.payment_method,
        bank: row.bank,
        reference: row.reference,
        sales_request: {
          id: row.sales_request_id,
          total_amount: row.sales_request_total_amount,
          payment_due_date: row.sales_request_payment_due_date,
          payment_terms: row.sales_request_payment_terms,
          delivery_date: row.sales_request_delivery_date,
          status: row.sales_request_status,
          paid: row.sales_request_paid,
          factura_number:
            row.factura_number !== "N/A" ? row.factura_number : null,
        },
        client: {
          name: row.client_name,
          tax_id: row.client_tax_id,
        },
        manual_factura: row.manual_factura_id
          ? {
              id: row.manual_factura_id,
              doc_number: row.manual_factura_doc_number,
            }
          : null,
        transfer_date: row.transfer_date,
        fintoc_reference_id: row.fintoc_reference_id,
      })) || [];

    console.log(
      "Exporting all payments - Total payments found:",
      allPayments.length
    );

    // Prepare detailed payments data using data from database function
    const detailedPaymentsData = allPayments.map(
      (payment: any, index: number) => {
        // Debug logging for first few payments
        if (index < 3) {
          console.log(`Payment ${index + 1} (ID: ${payment.id}):`, {
            verified_at: payment.verified_at,
            verified_at_type: typeof payment.verified_at,
            is_verified: payment.is_verified,
            created_at: payment.created_at,
          });
        }
        // Calculate payment status
        const paymentDate = payment.created_at
          ? new Date(payment.created_at)
          : null;
        const dueDate = payment.sales_request?.payment_due_date
          ? new Date(payment.sales_request.payment_due_date)
          : null;
        const isLate = dueDate && paymentDate && paymentDate > dueDate;

        // Get invoice number from database function result
        const numeroFactura = payment.sales_request?.factura_number || "N/A";

        // Get creator name from database function
        const creatorName = (paymentData as any)?.find(
          (row: any) => row.payment_id === payment.id
        );
        const vendedorName = creatorName
          ? `${creatorName.creator_name || ""} ${creatorName.creator_lastname || ""}`.trim()
          : "N/A";

        return {
          "ID Pago": payment.id,
          "Fecha Verificación": payment.verified_at
            ? (() => {
                const formattedDate = format(
                  new Date(payment.verified_at),
                  "dd/MM/yyyy HH:mm",
                  {
                    locale: es,
                  }
                );
                // Debug log for first few payments
                if (index < 3) {
                  console.log(
                    `Payment ${payment.id} verification date formatted:`,
                    formattedDate
                  );
                }
                return formattedDate;
              })()
            : "N/A",
          "Fecha Transferencia": payment.transfer_date
            ? format(new Date(payment.transfer_date), "dd/MM/yyyy", {
                locale: es,
              })
            : "N/A",
          "Referencia Transferencia": payment.fintoc_reference_id || "N/A",
          "Monto Pago": payment.amount || 0,
          "Método de Pago":
            translatePaymentMethod(payment.payment_method) || "N/A",
          Banco: payment.bank || "N/A",
          Referencia: payment.reference || "N/A",
          Cliente: payment.client?.name || "N/A",
          "RUT Cliente": payment.client?.tax_id || "N/A",
          "Monto Total NV": payment.sales_request?.total_amount || 0,
          "Fecha Vencimiento": payment.sales_request?.payment_due_date
            ? format(
                new Date(payment.sales_request.payment_due_date),
                "dd/MM/yyyy",
                { locale: es }
              )
            : "N/A",
          "Términos de Pago": payment.sales_request?.payment_terms
            ? `${payment.sales_request.payment_terms} días`
            : "N/A",
          "Fecha Entrega": payment.sales_request?.delivery_date
            ? format(
                new Date(payment.sales_request.delivery_date),
                "dd/MM/yyyy",
                { locale: es }
              )
            : "N/A",
          Vendedor: vendedorName,
          "Estado Pago": isLate ? "Atrasado" : "A Tiempo",
          "Días de Atraso":
            isLate && dueDate && paymentDate
              ? Math.ceil(
                  (paymentDate.getTime() - dueDate.getTime()) /
                    (1000 * 3600 * 24)
                )
              : 0,
          "Diferencia Pago vs NV":
            payment.amount && payment.sales_request?.total_amount
              ? payment.amount - payment.sales_request.total_amount
              : 0,
          "Porcentaje Cubierto":
            payment.amount && payment.sales_request?.total_amount
              ? Math.round(
                  (payment.amount / payment.sales_request.total_amount) * 100
                )
              : 0,
          "Número Factura": numeroFactura,
        };
      }
    );

    // Create workbook with only the detailed payments data
    const workbook = XLSX.utils.book_new();

    // Add detailed payments sheet as the main and only sheet
    if (detailedPaymentsData.length > 0) {
      const detailedPaymentsWorksheet =
        XLSX.utils.json_to_sheet(detailedPaymentsData);
      const detailedPaymentsColumnWidths = [
        { wch: 15 }, // ID Pago
        { wch: 20 }, // Fecha Verificación
        { wch: 20 }, // Fecha Transferencia
        { wch: 22 }, // Referencia Transferencia
        { wch: 15 }, // Monto Pago
        { wch: 18 }, // Método de Pago
        { wch: 18 }, // Banco
        { wch: 25 }, // Referencia
        { wch: 30 }, // Cliente
        { wch: 15 }, // RUT Cliente
        { wch: 18 }, // Monto Total NV
        { wch: 18 }, // Fecha Vencimiento
        { wch: 18 }, // Términos de Pago
        { wch: 18 }, // Fecha Entrega
        { wch: 25 }, // Vendedor
        { wch: 15 }, // Estado Pago
        { wch: 15 }, // Días de Atraso
        { wch: 20 }, // Diferencia Pago vs NV
        { wch: 18 }, // Porcentaje Cubierto
        { wch: 20 }, // Número Factura
      ];
      detailedPaymentsWorksheet["!cols"] = detailedPaymentsColumnWidths;
      XLSX.utils.book_append_sheet(
        workbook,
        detailedPaymentsWorksheet,
        "Reporte Detallado de Pagos"
      );
    } else {
      // If no payments data, create an empty sheet with headers
      const emptyData = [
        [
          "ID Pago",
          "Fecha Verificación",
          "Fecha Transferencia",
          "Referencia Transferencia",
          "Monto Pago",
          "Método de Pago",
          "Referencia",
          "Cliente",
          "RUT Cliente",
          "Monto Total NV",
          "Fecha Vencimiento",
          "Términos de Pago",
          "Fecha Entrega",
          "Vendedor",
          "Estado Pago",
          "Días de Atraso",
          "Diferencia Pago vs NV",
          "Porcentaje Cubierto",
          "Número Factura",
        ],
      ];
      const emptyWorksheet = XLSX.utils.aoa_to_sheet(emptyData);
      XLSX.utils.book_append_sheet(
        workbook,
        emptyWorksheet,
        "Reporte Detallado de Pagos"
      );
    }

    // Generate filename
    const currentDate = format(new Date(), "yyyy-MM-dd");
    const filename = `reporte-detallado-pagos-${currentDate}.xlsx`;

    // Write and download file
    XLSX.writeFile(workbook, filename);
  } catch (error) {
    console.error("Error exporting payments to Excel:", error);
    throw error;
  }
};

/**
 * Export payments data for BI analysis
 */
export const exportPaymentsForBI = async (
  payments: any[],
  latePayments: any[],
  outstandingPayments: any[],
  dateRange?: { from?: Date; to?: Date }
): Promise<void> => {
  try {
    // Prepare BI data with simplified structure
    const biData = [
      // Regular payments
      ...payments.map((payment) => {
        // Get factura number
        let numeroFactura = "N/A";
        if (payment.sales_request?.factura_number) {
          numeroFactura = payment.sales_request.factura_number;
        } else if (payment.manual_factura?.doc_number) {
          numeroFactura = payment.manual_factura.doc_number;
        }

        return {
          Tipo: "Pago",
          ID: payment.id,
          Fecha: payment.created_at,
          Fecha_Verificacion: payment.verified_at,
          Monto: payment.amount || 0,
          Cliente: payment.client?.name || "N/A",
          RUT_Cliente: payment.client?.tax_id || "N/A",
          Metodo_Pago: translatePaymentMethod(payment.payment_method) || "N/A",
          Banco: payment.bank || "N/A",
          Referencia: payment.reference || "N/A",
          Nota_Venta_ID: payment.sales_request?.id || "N/A",
          Manual_Factura_ID: payment.manual_factura?.id || "N/A",
          Numero_Factura: numeroFactura,
          Estado: payment.status || "N/A",
          Dias_Vencidos: 0,
          Interes_Generado: 0,
          Fuente: "Pago",
        };
      }),
      // Late payments
      ...latePayments.map((payment) => ({
        Tipo: "Pago_Atrasado",
        ID: payment.id,
        Fecha: payment.payment_due_date,
        Monto: payment.remaining_amount || 0,
        Cliente: payment.client?.name || "N/A",
        RUT_Cliente: payment.client?.tax_id || "N/A",
        Metodo_Pago: "N/A",
        Referencia: payment.doc_number || "N/A",
        Nota_Venta_ID: payment.source === "sales_request" ? payment.id : "N/A",
        Estado: "Atrasado",
        Dias_Vencidos: payment.daysLate || 0,
        Interes_Generado: payment.proportional_interest || 0,
        Fuente:
          payment.source === "sales_request" ? "Nota_Venta" : "Factura_Manual",
      })),
      // Outstanding payments
      ...outstandingPayments.map((payment) => ({
        Tipo: "Pago_Pendiente",
        ID: payment.id,
        Fecha: payment.payment_due_date,
        Monto: payment.remaining_amount || 0,
        Cliente: payment.client?.name || "N/A",
        RUT_Cliente: payment.client?.tax_id || "N/A",
        Metodo_Pago: "N/A",
        Referencia: payment.doc_number || "N/A",
        Nota_Venta_ID: payment.source === "sales_request" ? payment.id : "N/A",
        Estado: "Pendiente",
        Dias_Vencidos: 0,
        Interes_Generado: 0,
        Fuente:
          payment.source === "sales_request" ? "Nota_Venta" : "Factura_Manual",
      })),
    ];

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(biData);

    // Set column widths
    const columnWidths = [
      { wch: 18 }, // Tipo
      { wch: 15 }, // ID
      { wch: 20 }, // Fecha
      { wch: 20 }, // Fecha_Verificacion
      { wch: 15 }, // Monto
      { wch: 30 }, // Cliente
      { wch: 15 }, // RUT_Cliente
      { wch: 18 }, // Metodo_Pago
      { wch: 25 }, // Referencia
      { wch: 20 }, // Nota_Venta_ID
      { wch: 20 }, // Manual_Factura_ID
      { wch: 20 }, // Numero_Factura
      { wch: 15 }, // Estado
      { wch: 15 }, // Dias_Vencidos
      { wch: 18 }, // Interes_Generado
      { wch: 18 }, // Fuente
    ];
    worksheet["!cols"] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Pagos BI");

    // Generate filename
    const currentDate = format(new Date(), "yyyy-MM-dd");
    const filename = `pagos-bi-${currentDate}.xlsx`;

    // Write and download file
    XLSX.writeFile(workbook, filename);
  } catch (error) {
    console.error("Error exporting payments for BI:", error);
    throw error;
  }
};
